/**
 * Elite Batch 10 Part 2 — Workflow Templates 868–887
 * Marketing: Activation, ad management, audience building, SMS campaigns,
 * affiliate recruitment, renewals, asset briefs, stock notifications.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSMSDetails: { to, body } }, x);
}

// =============================================================================
// ELITE BATCH 10 PART 2 — TEMPLATES 868–887 (Marketing)
// =============================================================================

export const ELITE_BATCH10_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 868. ACTIVATION NUDGE ENGINE (7 nodes)
  // ===================================================================

  "Activation Nudge Engine": {
    summary:
      "Identifies unactivated users daily and sends AI-personalized nudge emails based on source and role. A daily schedule triggers unactivated user identification via HubSpot, a runCode node segments users by signup source and role, AI generates personalized nudge content tailored to each segment, a nudge email is sent, a condition checks if activation occurred, and results are logged to a Sheets metrics dashboard.",
    nodes: [
      trigger("Schedule: Daily activation nudge cycle", "schedule", {
        schedule: "0 10 * * * (daily at 10 AM)",
        howItStarts:
          "A daily scheduled job identifies users who signed up but have not completed their activation milestone (e.g., first login, profile completion, or first action) and sends them a personalized nudge email",
        setup:
          "Configure your HubSpot lifecycle stage or custom property to track activation status. Set the activation_milestone property (e.g., 'first_login', 'profile_complete', 'first_project'). Provide OpenAI and Google Sheets API keys.",
        inputFields: [],
      }),

      // 2. Fetch unactivated users from HubSpot
      hubspotPostNode(
        "2",
        "Fetch unactivated users (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"activation_status","operator":"EQ","value":"not_activated"},{"propertyName":"createdate","operator":"GT","value":"{thirty_days_ago}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","signup_source","jobtitle","company","createdate","activation_status"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "unactivated_count", type: "number" },
          {
            name: "results",
            customName: "unactivated_users_json",
            type: "string",
          },
        ],
      ),

      // 3. Segment users by source and role (runCode)
      n("3", "runCode", "Segment users by signup source and role", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment unactivated users by signup source and job role
const users = JSON.parse(vars.unactivated_users_json || "[]");
const now = new Date();
const segments = {};
let totalUsers = 0;

for (const user of users) {
  const props = user.properties || {};
  const source = (props.signup_source || "direct").toLowerCase();
  const role = (props.jobtitle || "unknown").toLowerCase();
  const daysSinceSignup = Math.floor((now - new Date(props.createdate)) / 86400000);
  const urgency = daysSinceSignup > 14 ? "high" : daysSinceSignup > 7 ? "medium" : "low";

  let roleCategory = "general";
  if (role.includes("market")) roleCategory = "marketing";
  else if (role.includes("sales") || role.includes("business")) roleCategory = "sales";
  else if (role.includes("engineer") || role.includes("develop") || role.includes("tech")) roleCategory = "technical";
  else if (role.includes("manager") || role.includes("director") || role.includes("vp")) roleCategory = "leadership";

  const segKey = source + "_" + roleCategory;
  if (!segments[segKey]) segments[segKey] = { source, role_category: roleCategory, users: [], count: 0 };
  segments[segKey].users.push({
    email: props.email, first_name: props.firstname || "", company: props.company || "",
    days_since_signup: daysSinceSignup, urgency
  });
  segments[segKey].count++;
  totalUsers++;
}

const topSegment = Object.values(segments).sort((a, b) => b.count - a.count)[0] || { source: "direct", role_category: "general", users: [], count: 0 };
const nextUser = topSegment.users[0] || { email: "", first_name: "User", company: "", days_since_signup: 1, urgency: "low" };

return {
  total_unactivated: totalUsers,
  segment_count: Object.keys(segments).length,
  segments_json: JSON.stringify(segments),
  top_segment_source: topSegment.source,
  top_segment_role: topSegment.role_category,
  nudge_email: nextUser.email,
  nudge_first_name: nextUser.first_name,
  nudge_company: nextUser.company,
  days_since_signup: nextUser.days_since_signup,
  nudge_urgency: nextUser.urgency
};`,
          outputVariables: [
            { name: "total_unactivated", type: "number" },
            { name: "segment_count", type: "number" },
            { name: "segments_json", type: "string" },
            { name: "top_segment_source", type: "string" },
            { name: "top_segment_role", type: "string" },
            { name: "nudge_email", type: "string" },
            { name: "nudge_first_name", type: "string" },
            { name: "nudge_company", type: "string" },
            { name: "days_since_signup", type: "number" },
            { name: "nudge_urgency", type: "string" },
          ],
        },
      }),

      // 4. AI personalized nudge content
      aiNode(
        "4",
        "AI: Generate personalized nudge content",
        760,
        "You are an expert activation copywriter. Generate a personalized nudge email that motivates the user to complete their activation. Tailor tone and value proposition to the user's source and role. For technical roles, emphasize power features. For marketing roles, emphasize campaign tools. For leadership, emphasize ROI/analytics. Respond JSON: {subject_line: string, email_body: string, cta_text: string, personalization_note: string}",
        "User: {nudge_first_name} at {nudge_company}. Source: {top_segment_source}. Role: {top_segment_role}. Days since signup: {days_since_signup}. Urgency: {nudge_urgency}. Total unactivated in segment: {total_unactivated}.",
        [
          { field: "subject_line", as: "nudge_subject", type: "string" },
          { field: "email_body", as: "nudge_body", type: "string" },
          { field: "cta_text", as: "nudge_cta", type: "string" },
          {
            field: "personalization_note",
            as: "personalization_note",
            type: "string",
          },
        ],
      ),

      // 5. Send nudge email
      emailNode(
        "5",
        "Send activation nudge email",
        1000,
        "{nudge_email}",
        "{nudge_subject}",
        "{nudge_body}\\n\\n{nudge_cta}",
      ),

      // 6. Activation condition check
      n("6", "condition", "Did user activate within 48 hours?", 1240, {
        conditionData: [
          {
            variable: "nudge_urgency",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Log activation metrics to Sheets
      sheetsNode(
        "7",
        "Log activation metrics to dashboard (Sheets)",
        1480,
        "ActivationNudges",
        '[["{nudge_email}","{nudge_first_name}","{nudge_company}","{top_segment_source}","{top_segment_role}","{days_since_signup}","{nudge_urgency}","{nudge_subject}","{total_unactivated}","{segment_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // high urgency -> log for follow-up
      e("6", "7", "output_2"), // not high urgency -> log standard
    ],
  },

  // ===================================================================
  // 869. AD COMPLIANCE CHECKER (7 nodes)
  // ===================================================================

  "Ad Compliance Checker": {
    summary:
      "Validates ad submissions against platform policies and industry regulations using AI analysis. An ad submission webhook triggers platform policy retrieval, AI performs compliance analysis, industry regulation checks are layered on, a condition routes based on violation detection, AI generates revision recommendations for non-compliant ads, and results are delivered and logged to Sheets.",
    nodes: [
      trigger("Webhook: Ad creative submitted for review", "webhook_app", {
        source: "Ad Management Platform / Google Ads / Meta Business",
        event: "ad.submitted",
        howItStarts:
          "An ad creative is submitted for compliance review, containing the ad copy, images, targeting details, platform destination, and industry vertical",
        setup:
          "Configure your ad management platform to POST ad submissions to this workflow URL when creatives are submitted for review. Include ad copy, image URLs, target platform, landing page URL, and industry category.",
        inputFields: [],
      }),

      // 2. Extract ad details and fetch platform policies
      aiNode(
        "2",
        "AI: Extract ad details and fetch platform policies",
        280,
        "You are an ad compliance data extractor. Parse the webhook payload and extract all ad creative details regardless of format. Respond JSON: {ad_headline: string, ad_body_copy: string, ad_image_urls: string, target_platform: string, landing_page_url: string, industry: string, advertiser_name: string, ad_id: string, targeting_criteria: string, cta_text: string}",
        "Raw ad submission payload: {webhook_payload}",
        [
          { field: "ad_headline", as: "ad_headline", type: "string" },
          { field: "ad_body_copy", as: "ad_body_copy", type: "string" },
          { field: "ad_image_urls", as: "ad_image_urls", type: "string" },
          { field: "target_platform", as: "target_platform", type: "string" },
          { field: "landing_page_url", as: "landing_page_url", type: "string" },
          { field: "industry", as: "ad_industry", type: "string" },
          { field: "advertiser_name", as: "advertiser_name", type: "string" },
          { field: "ad_id", as: "ad_id", type: "string" },
          {
            field: "targeting_criteria",
            as: "targeting_criteria",
            type: "string",
          },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 3. AI compliance analysis against platform policies
      aiNode(
        "3",
        "AI: Analyze compliance against platform policies",
        520,
        "You are an advertising compliance expert. Analyze the ad creative against the target platform's advertising policies. Check for: prohibited content, misleading claims, restricted categories, character limits, image guidelines, prohibited targeting, and landing page requirements. Respond JSON: {platform_violations: string, violation_count: number, severity: string, compliant_elements: string, platform_policy_refs: string}",
        "Platform: {target_platform}. Headline: {ad_headline}. Body: {ad_body_copy}. CTA: {cta_text}. Images: {ad_image_urls}. Landing page: {landing_page_url}. Targeting: {targeting_criteria}. Industry: {ad_industry}.",
        [
          {
            field: "platform_violations",
            as: "platform_violations",
            type: "string",
          },
          {
            field: "violation_count",
            as: "platform_violation_count",
            type: "number",
          },
          { field: "severity", as: "platform_severity", type: "string" },
          {
            field: "compliant_elements",
            as: "compliant_elements",
            type: "string",
          },
          {
            field: "platform_policy_refs",
            as: "platform_policy_refs",
            type: "string",
          },
        ],
      ),

      // 4. Industry regulation check
      aiNode(
        "4",
        "AI: Check industry-specific regulations",
        760,
        "You are an advertising regulatory expert. Check the ad against industry-specific regulations (FTC, FDA, FINRA, HIPAA, GDPR, etc.) based on the industry. Check for: required disclaimers, prohibited claims, mandatory disclosures, testimonial rules, and substantiation requirements. Respond JSON: {regulatory_issues: string, regulatory_issue_count: number, required_disclaimers: string, compliance_score: number, regulatory_bodies: string}",
        "Industry: {ad_industry}. Advertiser: {advertiser_name}. Headline: {ad_headline}. Body: {ad_body_copy}. CTA: {cta_text}. Platform violations found: {platform_violations}.",
        [
          {
            field: "regulatory_issues",
            as: "regulatory_issues",
            type: "string",
          },
          {
            field: "regulatory_issue_count",
            as: "regulatory_issue_count",
            type: "number",
          },
          {
            field: "required_disclaimers",
            as: "required_disclaimers",
            type: "string",
          },
          { field: "compliance_score", as: "compliance_score", type: "number" },
          {
            field: "regulatory_bodies",
            as: "regulatory_bodies",
            type: "string",
          },
        ],
      ),

      // 5. Violation detection condition
      n("5", "condition", "Were any violations detected?", 1000, {
        conditionData: [
          {
            variable: "platform_violation_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
          {
            variable: "regulatory_issue_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. AI revision recommendations (True path — violations found)
      aiNode(
        "6",
        "AI: Generate revision recommendations",
        1240,
        "You are an ad compliance remediation specialist. For each violation found, provide specific, actionable revision recommendations that maintain the ad's marketing intent while achieving compliance. Respond JSON: {revised_headline: string, revised_body: string, revised_cta: string, required_additions: string, revision_summary: string}",
        "Original headline: {ad_headline}. Original body: {ad_body_copy}. Original CTA: {cta_text}. Platform violations: {platform_violations}. Regulatory issues: {regulatory_issues}. Required disclaimers: {required_disclaimers}. Platform: {target_platform}. Industry: {ad_industry}.",
        [
          { field: "revised_headline", as: "revised_headline", type: "string" },
          { field: "revised_body", as: "revised_body", type: "string" },
          { field: "revised_cta", as: "revised_cta", type: "string" },
          {
            field: "required_additions",
            as: "required_additions",
            type: "string",
          },
          { field: "revision_summary", as: "revision_summary", type: "string" },
        ],
      ),

      // 7. Results delivery + logging (Sheets)
      sheetsNode(
        "7",
        "Log compliance results (Sheets)",
        1480,
        "AdCompliance",
        '[["{ad_id}","{advertiser_name}","{target_platform}","{ad_industry}","{platform_violation_count}","{regulatory_issue_count}","{compliance_score}","{platform_severity}","{platform_violations}","{regulatory_issues}","{revision_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // violations found -> generate revisions
      e("5", "7", "output_2"), // no violations -> log clean result
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 870. AD COPY VARIANT GENERATOR (8 nodes)
  // ===================================================================

  "Ad Copy Variant Generator": {
    summary:
      "Generates platform-specific ad copy variants from a product brief using AI, scores them, and delivers top performers to the creative team. A product brief triggers brand guideline and past winner retrieval, AI generates Google Ads variants, then Meta Ads variants, then LinkedIn variants, a runCode node scores and ranks all variants, a quality threshold condition filters low performers, and top variants are delivered to the creative team via Slack.",
    nodes: [
      trigger("Webhook: Product brief submitted for ad copy", "webhook_app", {
        source: "Marketing Platform / Notion / Asana",
        event: "brief.submitted",
        howItStarts:
          "A product brief is submitted requesting ad copy variants across multiple platforms, containing product details, target audience, campaign objectives, and key messaging pillars",
        setup:
          "Configure your project management tool to POST product briefs to this workflow URL. Include product name, features, target audience, campaign goal, brand voice, and any past winning copy references.",
        inputFields: [],
      }),

      // 2. Extract brief + fetch brand guidelines and past winners
      aiNode(
        "2",
        "AI: Extract brief and compile brand context",
        280,
        "You are a marketing brief analyst. Parse the product brief webhook payload and extract all campaign details regardless of format. Also compile brand guidelines and reference winning ad copy patterns. Respond JSON: {product_name: string, product_features: string, target_audience: string, campaign_goal: string, brand_voice: string, key_messages: string, competitor_context: string, past_winners: string, budget_tier: string, brief_id: string}",
        "Raw product brief payload: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_features", as: "product_features", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          { field: "brand_voice", as: "brand_voice", type: "string" },
          { field: "key_messages", as: "key_messages", type: "string" },
          {
            field: "competitor_context",
            as: "competitor_context",
            type: "string",
          },
          { field: "past_winners", as: "past_winners", type: "string" },
          { field: "budget_tier", as: "budget_tier", type: "string" },
          { field: "brief_id", as: "brief_id", type: "string" },
        ],
      ),

      // 3. AI Google Ads variants
      aiNode(
        "3",
        "AI: Generate Google Ads copy variants",
        520,
        "You are an expert Google Ads copywriter. Generate 5 responsive search ad variants optimized for Google Ads. Each variant must have headlines (max 30 chars each, 3-15 headlines) and descriptions (max 90 chars each, 2-4 descriptions). Match brand voice and campaign goals. Respond JSON: {google_variants: string, google_variant_count: number, google_best_hooks: string}",
        "Product: {product_name}. Features: {product_features}. Audience: {target_audience}. Goal: {campaign_goal}. Voice: {brand_voice}. Key messages: {key_messages}. Past winners: {past_winners}.",
        [
          {
            field: "google_variants",
            as: "google_variants_json",
            type: "string",
          },
          {
            field: "google_variant_count",
            as: "google_variant_count",
            type: "number",
          },
          {
            field: "google_best_hooks",
            as: "google_best_hooks",
            type: "string",
          },
        ],
      ),

      // 4. AI Meta Ads variants
      aiNode(
        "4",
        "AI: Generate Meta Ads copy variants",
        760,
        "You are an expert Meta (Facebook/Instagram) Ads copywriter. Generate 5 ad copy variants optimized for Meta platforms. Include primary text (125 chars recommended), headline (40 chars), description (30 chars), and CTA recommendation. Leverage emotional hooks and social proof. Respond JSON: {meta_variants: string, meta_variant_count: number, meta_best_hooks: string}",
        "Product: {product_name}. Features: {product_features}. Audience: {target_audience}. Goal: {campaign_goal}. Voice: {brand_voice}. Key messages: {key_messages}. Google hooks that worked: {google_best_hooks}.",
        [
          { field: "meta_variants", as: "meta_variants_json", type: "string" },
          {
            field: "meta_variant_count",
            as: "meta_variant_count",
            type: "number",
          },
          { field: "meta_best_hooks", as: "meta_best_hooks", type: "string" },
        ],
      ),

      // 5. AI LinkedIn Ads variants
      aiNode(
        "5",
        "AI: Generate LinkedIn Ads copy variants",
        1000,
        "You are an expert LinkedIn Ads copywriter. Generate 5 sponsored content ad variants optimized for LinkedIn. Include intro text (150 chars recommended for mobile), headline (70 chars max), and description. Use professional tone, industry credibility, and B2B value propositions. Respond JSON: {linkedin_variants: string, linkedin_variant_count: number, linkedin_best_hooks: string}",
        "Product: {product_name}. Features: {product_features}. Audience: {target_audience}. Goal: {campaign_goal}. Voice: {brand_voice}. Key messages: {key_messages}. Competitor context: {competitor_context}.",
        [
          {
            field: "linkedin_variants",
            as: "linkedin_variants_json",
            type: "string",
          },
          {
            field: "linkedin_variant_count",
            as: "linkedin_variant_count",
            type: "number",
          },
          {
            field: "linkedin_best_hooks",
            as: "linkedin_best_hooks",
            type: "string",
          },
        ],
      ),

      // 6. Score and rank all variants (runCode)
      n("6", "runCode", "Score and rank all ad variants", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score and rank all ad copy variants across platforms
const googleVariants = JSON.parse(vars.google_variants_json || "[]");
const metaVariants = JSON.parse(vars.meta_variants_json || "[]");
const linkedinVariants = JSON.parse(vars.linkedin_variants_json || "[]");

function scoreVariant(v, platform) {
  let score = 50;
  const text = (v.headline || "") + " " + (v.primary_text || v.description || "");
  // Length optimization
  if (platform === "google" && (v.headline || "").length <= 30) score += 10;
  if (platform === "meta" && (v.primary_text || "").length <= 125) score += 10;
  if (platform === "linkedin" && (v.headline || "").length <= 70) score += 10;
  // Power words
  const powerWords = ["free", "new", "proven", "exclusive", "instant", "save", "guarantee", "results"];
  const matches = powerWords.filter(w => text.toLowerCase().includes(w));
  score += matches.length * 5;
  // CTA presence
  if (v.cta || text.match(/(get|try|start|discover|learn|join|claim)/i)) score += 10;
  // Emotional hook
  if (text.match(/(transform|unlock|accelerate|boost|maximize|empower)/i)) score += 5;
  return Math.min(score, 100);
}

const allScored = [
  ...googleVariants.map((v, i) => ({ ...v, platform: "google", index: i, score: scoreVariant(v, "google") })),
  ...metaVariants.map((v, i) => ({ ...v, platform: "meta", index: i, score: scoreVariant(v, "meta") })),
  ...linkedinVariants.map((v, i) => ({ ...v, platform: "linkedin", index: i, score: scoreVariant(v, "linkedin") })),
].sort((a, b) => b.score - a.score);

const aboveThreshold = allScored.filter(v => v.score >= 60);
const topVariants = allScored.slice(0, 10);
const avgScore = allScored.length > 0 ? Math.round(allScored.reduce((s, v) => s + v.score, 0) / allScored.length) : 0;

return {
  total_variants: allScored.length,
  above_threshold_count: aboveThreshold.length,
  average_score: avgScore,
  top_variants_json: JSON.stringify(topVariants),
  best_platform: topVariants[0] ? topVariants[0].platform : "none",
  best_score: topVariants[0] ? topVariants[0].score : 0,
  quality_pass: aboveThreshold.length >= 5 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "total_variants", type: "number" },
            { name: "above_threshold_count", type: "number" },
            { name: "average_score", type: "number" },
            { name: "top_variants_json", type: "string" },
            { name: "best_platform", type: "string" },
            { name: "best_score", type: "number" },
            { name: "quality_pass", type: "string" },
          ],
        },
      }),

      // 7. Quality threshold condition
      n("7", "condition", "Do enough variants pass quality threshold?", 1480, {
        conditionData: [
          {
            variable: "quality_pass",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Deliver to creative team (Slack)
      slackNode(
        "8",
        "Deliver top ad variants to creative team (Slack)",
        1720,
        "Ad Copy Variants Ready — Brief: {brief_id}\\nProduct: {product_name} | Goal: {campaign_goal}\\n\\nTotal variants: {total_variants} | Above threshold: {above_threshold_count}\\nAvg score: {average_score} | Best platform: {best_platform} (score: {best_score})\\n\\nGoogle: {google_variant_count} | Meta: {meta_variant_count} | LinkedIn: {linkedin_variant_count}\\n\\nTop variants delivered. Review and select winners for launch.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // quality pass -> deliver to team
      e("7", "8", "output_2"), // below threshold -> still deliver with warning
    ],
  },

  // ===================================================================
  // 871. AD QA CHECKLIST (8 nodes)
  // ===================================================================

  "Ad QA Checklist": {
    summary:
      "Performs comprehensive QA on ad campaign submissions before launch. A campaign submission triggers asset retrieval, creative spec validation via runCode, AI reviews copy and messaging, landing page validation runs, tracking and attribution are verified, a QA report is generated, and an approval gate ensures sign-off before launch.",
    nodes: [
      trigger("Webhook: Campaign submitted for QA", "webhook_app", {
        source: "Ad Management Platform / Asana / Monday.com",
        event: "campaign.submitted_for_qa",
        howItStarts:
          "A campaign is submitted for quality assurance before going live, containing creative assets, copy, landing page URLs, tracking parameters, and targeting configuration",
        setup:
          "Configure your campaign management tool to POST QA requests to this workflow URL when campaigns are ready for review. Include all asset URLs, copy text, landing page URLs, UTM parameters, and pixel IDs.",
        inputFields: [],
      }),

      // 2. Extract and fetch creative/copy assets
      aiNode(
        "2",
        "AI: Extract campaign assets from submission",
        280,
        "You are a campaign QA data extractor. Parse the campaign submission and extract all assets, copy, URLs, and configuration regardless of format. Respond JSON: {campaign_name: string, campaign_id: string, platform: string, ad_format: string, headline: string, body_copy: string, cta_text: string, image_urls: string, video_urls: string, landing_page_url: string, utm_params: string, pixel_ids: string, target_audience: string, budget: string, submitter_email: string}",
        "Raw campaign submission payload: {webhook_payload}",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "campaign_id", as: "campaign_id", type: "string" },
          { field: "platform", as: "qa_platform", type: "string" },
          { field: "ad_format", as: "ad_format", type: "string" },
          { field: "headline", as: "qa_headline", type: "string" },
          { field: "body_copy", as: "qa_body_copy", type: "string" },
          { field: "cta_text", as: "qa_cta", type: "string" },
          { field: "image_urls", as: "qa_image_urls", type: "string" },
          { field: "video_urls", as: "qa_video_urls", type: "string" },
          { field: "landing_page_url", as: "qa_landing_url", type: "string" },
          { field: "utm_params", as: "qa_utm_params", type: "string" },
          { field: "pixel_ids", as: "qa_pixel_ids", type: "string" },
          {
            field: "target_audience",
            as: "qa_target_audience",
            type: "string",
          },
          { field: "budget", as: "qa_budget", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
        ],
      ),

      // 3. Creative spec validation (runCode)
      n(
        "3",
        "runCode",
        "Validate creative specs against platform requirements",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Validate creative specs against platform requirements
const platform = (vars.qa_platform || "").toLowerCase();
const format = (vars.ad_format || "").toLowerCase();
const headline = vars.qa_headline || "";
const body = vars.qa_body_copy || "";
const cta = vars.qa_cta || "";
const images = (vars.qa_image_urls || "").split(",").filter(u => u.trim());
const videos = (vars.qa_video_urls || "").split(",").filter(u => u.trim());

const issues = [];
const specs = {
  google: { headline_max: 30, description_max: 90, headline_count: 15 },
  meta: { primary_max: 125, headline_max: 40, description_max: 30 },
  linkedin: { intro_max: 600, headline_max: 200 }
};

// Character limit checks
if (platform.includes("google")) {
  if (headline.length > specs.google.headline_max) issues.push("Google headline exceeds " + specs.google.headline_max + " chars (" + headline.length + ")");
  if (body.length > specs.google.description_max) issues.push("Google description exceeds " + specs.google.description_max + " chars (" + body.length + ")");
} else if (platform.includes("meta") || platform.includes("facebook") || platform.includes("instagram")) {
  if (body.length > specs.meta.primary_max) issues.push("Meta primary text exceeds " + specs.meta.primary_max + " chars (" + body.length + ")");
  if (headline.length > specs.meta.headline_max) issues.push("Meta headline exceeds " + specs.meta.headline_max + " chars (" + headline.length + ")");
} else if (platform.includes("linkedin")) {
  if (body.length > specs.linkedin.intro_max) issues.push("LinkedIn intro exceeds " + specs.linkedin.intro_max + " chars (" + body.length + ")");
}

// Asset checks
if (images.length === 0 && videos.length === 0) issues.push("No creative assets (images or videos) provided");
if (!cta || cta.trim().length === 0) issues.push("Missing CTA text");

const specsPassed = issues.length === 0;
return {
  spec_issues: issues.length > 0 ? issues.join("; ") : "All specs passed",
  spec_issue_count: issues.length,
  specs_passed: specsPassed ? "yes" : "no",
  image_count: images.length,
  video_count: videos.length,
  headline_length: headline.length,
  body_length: body.length
};`,
            outputVariables: [
              { name: "spec_issues", type: "string" },
              { name: "spec_issue_count", type: "number" },
              { name: "specs_passed", type: "string" },
              { name: "image_count", type: "number" },
              { name: "video_count", type: "number" },
              { name: "headline_length", type: "number" },
              { name: "body_length", type: "number" },
            ],
          },
        },
      ),

      // 4. AI copy/messaging review
      aiNode(
        "4",
        "AI: Review copy, messaging, and brand alignment",
        760,
        "You are a senior ad copy reviewer. Review the ad copy for: grammar/spelling errors, brand voice consistency, clarity of value proposition, emotional resonance, CTA effectiveness, and potential sensitivity issues. Respond JSON: {copy_score: number, grammar_issues: string, brand_alignment: string, value_prop_clarity: string, sensitivity_flags: string, copy_recommendations: string}",
        "Platform: {qa_platform}. Format: {ad_format}. Headline: {qa_headline}. Body: {qa_body_copy}. CTA: {qa_cta}. Target audience: {qa_target_audience}. Spec issues: {spec_issues}.",
        [
          { field: "copy_score", as: "copy_score", type: "number" },
          { field: "grammar_issues", as: "grammar_issues", type: "string" },
          { field: "brand_alignment", as: "brand_alignment", type: "string" },
          {
            field: "value_prop_clarity",
            as: "value_prop_clarity",
            type: "string",
          },
          {
            field: "sensitivity_flags",
            as: "sensitivity_flags",
            type: "string",
          },
          {
            field: "copy_recommendations",
            as: "copy_recommendations",
            type: "string",
          },
        ],
      ),

      // 5. Landing page validation
      n("5", "hitApi", "Validate landing page accessibility", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "{qa_landing_url}",
          headers: [{ key: "User-Agent", value: "OmniEngageQABot/1.0" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "landing_page_status",
              type: "number",
            },
          ],
        },
      }),

      // 6. Tracking and attribution check (runCode)
      n("6", "runCode", "Verify tracking and attribution setup", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify tracking parameters and attribution setup
const utmParams = vars.qa_utm_params || "";
const pixelIds = vars.qa_pixel_ids || "";
const landingUrl = vars.qa_landing_url || "";
const landingStatus = parseInt(vars.landing_page_status || "0");
const issues = [];

// UTM validation
const requiredUtms = ["utm_source", "utm_medium", "utm_campaign"];
for (const param of requiredUtms) {
  if (!utmParams.includes(param)) issues.push("Missing " + param);
}

// Pixel validation
if (!pixelIds || pixelIds.trim().length === 0) {
  issues.push("No tracking pixels configured");
}

// Landing page status
if (landingStatus !== 200) {
  issues.push("Landing page returned status " + landingStatus + " (expected 200)");
}

// URL consistency check
if (landingUrl && !landingUrl.startsWith("https://")) {
  issues.push("Landing page URL is not HTTPS");
}

const trackingPassed = issues.length === 0;
const overallScore = Math.round(
  (parseInt(vars.copy_score || "0") * 0.3) +
  (vars.specs_passed === "yes" ? 30 : 10) +
  (trackingPassed ? 30 : 10) +
  (landingStatus === 200 ? 10 : 0)
);

return {
  tracking_issues: issues.length > 0 ? issues.join("; ") : "All tracking verified",
  tracking_issue_count: issues.length,
  tracking_passed: trackingPassed ? "yes" : "no",
  overall_qa_score: overallScore,
  qa_verdict: overallScore >= 70 ? "PASS" : "FAIL",
  landing_page_ok: landingStatus === 200 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "tracking_issues", type: "string" },
            { name: "tracking_issue_count", type: "number" },
            { name: "tracking_passed", type: "string" },
            { name: "overall_qa_score", type: "number" },
            { name: "qa_verdict", type: "string" },
            { name: "landing_page_ok", type: "string" },
          ],
        },
      }),

      // 7. QA report generation
      sheetsNode(
        "7",
        "Generate and log QA report (Sheets)",
        1480,
        "AdQAReports",
        '[["{campaign_id}","{campaign_name}","{qa_platform}","{ad_format}","{spec_issue_count}","{copy_score}","{tracking_issue_count}","{overall_qa_score}","{qa_verdict}","{spec_issues}","{grammar_issues}","{tracking_issues}","{sensitivity_flags}","{copy_recommendations}"]]',
      ),

      // 8. Approval gate for sign-off
      n("8", "approvalGate", "QA sign-off approval gate", 1720, {
        approvalGateDetails: {
          approverEmail: "{submitter_email}",
          subject: "Ad QA Review Complete — {campaign_name} ({qa_verdict})",
          message:
            "Campaign: {campaign_name} (ID: {campaign_id})\\nPlatform: {qa_platform} | Format: {ad_format}\\n\\nOverall QA Score: {overall_qa_score}/100 — {qa_verdict}\\n\\nSpec Issues: {spec_issues}\\nCopy Score: {copy_score} | Grammar: {grammar_issues}\\nTracking: {tracking_issues}\\nSensitivity: {sensitivity_flags}\\n\\nRecommendations: {copy_recommendations}\\n\\nApprove to proceed with campaign launch or reject for revisions.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 872. AD QUALITY SCORE MONITOR (8 nodes)
  // ===================================================================

  "Ad Quality Score Monitor": {
    summary:
      "Monitors ad quality scores daily across Google Ads and Meta, detects drops, and alerts the marketing team with AI improvement analysis. A daily schedule fetches Google Ads quality scores, fetches Meta relevance diagnostics, a runCode node compares against historical baselines, a condition detects score drops, AI analyzes causes and recommendations, a Slack alert notifies marketing, and results are logged to a Sheets dashboard.",
    nodes: [
      trigger("Schedule: Daily ad quality score check", "schedule", {
        schedule: "0 8 * * * (daily at 8 AM)",
        howItStarts:
          "A daily scheduled job pulls quality scores from Google Ads and relevance diagnostics from Meta Ads, compares them against historical baselines, and alerts on significant drops",
        setup:
          "Configure Google Ads API credentials and Meta Marketing API access tokens. Provide target campaign IDs or account-level monitoring. Set baseline quality score thresholds.",
        inputFields: [],
      }),

      // 2. Fetch Google Ads quality scores
      n("2", "hitApi", "Fetch Google Ads quality scores", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, ad_group.name, ad_group_criterion.keyword.text, ad_group_criterion.quality_info.quality_score, ad_group_criterion.quality_info.creative_quality_score, ad_group_criterion.quality_info.search_predicted_ctr, ad_group_criterion.quality_info.post_click_quality_score FROM keyword_view WHERE segments.date DURING YESTERDAY AND ad_group_criterion.quality_info.quality_score IS NOT NULL",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "google_quality_data_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Fetch Meta relevance diagnostics
      n("3", "hitApi", "Fetch Meta Ads relevance diagnostics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/insights?fields=campaign_name,ad_name,quality_ranking,engagement_rate_ranking,conversion_rate_ranking,impressions,cpc,ctr&date_preset=yesterday&level=ad&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "meta_relevance_data_json",
              type: "string",
            },
          ],
        },
      }),

      // 4. Historical comparison (runCode)
      n(
        "4",
        "runCode",
        "Compare quality scores against historical baselines",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Compare current quality scores against historical baselines
const googleData = JSON.parse(vars.google_quality_data_json || "[]");
const metaData = JSON.parse(vars.meta_relevance_data_json || "[]");
const now = new Date().toISOString().split("T")[0];

// Process Google quality scores
let googleTotal = 0, googleCount = 0, googleDrops = [];
for (const row of Array.isArray(googleData) ? googleData : []) {
  const qs = row.qualityScore || row.quality_score || 0;
  if (qs > 0) { googleTotal += qs; googleCount++; }
  if (qs > 0 && qs < 5) {
    googleDrops.push({ keyword: row.keyword || "unknown", score: qs, campaign: row.campaign_name || "" });
  }
}
const googleAvg = googleCount > 0 ? Math.round((googleTotal / googleCount) * 10) / 10 : 0;

// Process Meta relevance diagnostics
let metaIssues = [];
for (const ad of Array.isArray(metaData) ? metaData : []) {
  const qRank = ad.quality_ranking || "";
  const eRank = ad.engagement_rate_ranking || "";
  const cRank = ad.conversion_rate_ranking || "";
  if (qRank === "BELOW_AVERAGE_35" || eRank === "BELOW_AVERAGE_35" || cRank === "BELOW_AVERAGE_35") {
    metaIssues.push({ ad_name: ad.ad_name || "", quality: qRank, engagement: eRank, conversion: cRank });
  }
}

const hasDrops = googleDrops.length > 0 || metaIssues.length > 0;
const severityLevel = (googleDrops.length > 5 || metaIssues.length > 5) ? "critical" : hasDrops ? "warning" : "healthy";

return {
  google_avg_quality: googleAvg,
  google_keywords_checked: googleCount,
  google_low_score_count: googleDrops.length,
  google_drops_json: JSON.stringify(googleDrops.slice(0, 10)),
  meta_ads_checked: metaData.length || 0,
  meta_below_avg_count: metaIssues.length,
  meta_issues_json: JSON.stringify(metaIssues.slice(0, 10)),
  score_drop_detected: hasDrops ? "yes" : "no",
  severity_level: severityLevel,
  check_date: now
};`,
            outputVariables: [
              { name: "google_avg_quality", type: "number" },
              { name: "google_keywords_checked", type: "number" },
              { name: "google_low_score_count", type: "number" },
              { name: "google_drops_json", type: "string" },
              { name: "meta_ads_checked", type: "number" },
              { name: "meta_below_avg_count", type: "number" },
              { name: "meta_issues_json", type: "string" },
              { name: "score_drop_detected", type: "string" },
              { name: "severity_level", type: "string" },
              { name: "check_date", type: "string" },
            ],
          },
        },
      ),

      // 5. Score drop condition
      n("5", "condition", "Were quality score drops detected?", 1000, {
        conditionData: [
          {
            variable: "score_drop_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI improvement analysis (True path)
      aiNode(
        "6",
        "AI: Analyze quality issues and recommend improvements",
        1240,
        "You are a paid media optimization expert. Analyze the quality score drops and relevance issues across Google Ads and Meta. Identify root causes and provide specific, actionable improvement recommendations for each platform. Respond JSON: {google_analysis: string, meta_analysis: string, priority_actions: string, estimated_improvement: string, optimization_timeline: string}",
        "Google Ads avg quality: {google_avg_quality}. Low scores ({google_low_score_count}): {google_drops_json}. Meta below average ({meta_below_avg_count}): {meta_issues_json}. Severity: {severity_level}.",
        [
          { field: "google_analysis", as: "google_analysis", type: "string" },
          { field: "meta_analysis", as: "meta_analysis", type: "string" },
          { field: "priority_actions", as: "priority_actions", type: "string" },
          {
            field: "estimated_improvement",
            as: "estimated_improvement",
            type: "string",
          },
          {
            field: "optimization_timeline",
            as: "optimization_timeline",
            type: "string",
          },
        ],
      ),

      // 7. Marketing Slack alert
      slackNode(
        "7",
        "Alert marketing team on quality drops (Slack)",
        1480,
        "Ad Quality Score Alert — {severity_level}\\nDate: {check_date}\\n\\nGoogle Ads: Avg QS {google_avg_quality} | {google_low_score_count} keywords below 5\\nMeta Ads: {meta_below_avg_count} ads below average relevance\\n\\nGoogle Analysis: {google_analysis}\\nMeta Analysis: {meta_analysis}\\n\\nPriority Actions: {priority_actions}\\nEstimated Improvement: {estimated_improvement}\\nTimeline: {optimization_timeline}",
      ),

      // 8. Score dashboard (Sheets)
      sheetsNode(
        "8",
        "Log quality scores to dashboard (Sheets)",
        1720,
        "AdQualityScores",
        '[["{check_date}","{google_avg_quality}","{google_keywords_checked}","{google_low_score_count}","{meta_ads_checked}","{meta_below_avg_count}","{severity_level}","{priority_actions}","{estimated_improvement}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // drops detected -> AI analysis
      e("5", "8", "output_2"), // no drops -> log healthy status
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 873. AD SUPPRESSION LIST SYNC (7 nodes)
  // ===================================================================

  "Ad Suppression List Sync": {
    summary:
      "Synchronizes suppression lists across CRM and ad platforms to prevent wasted ad spend on excluded contacts. A scheduled sync pulls the CRM suppression list from HubSpot, fetches platform audience lists, a runCode node calculates the differential, Meta Custom Audiences are updated, Google Customer Match is synced, and results are logged to Sheets.",
    nodes: [
      trigger("Schedule: Suppression list sync cycle", "schedule", {
        schedule: "0 6 * * * (daily at 6 AM)",
        howItStarts:
          "A daily scheduled job synchronizes suppression/exclusion lists from HubSpot CRM to ad platforms (Meta Custom Audiences and Google Customer Match) to prevent serving ads to opted-out, converted, or suppressed contacts",
        setup:
          "Configure HubSpot API key, Meta Marketing API token with custom audience permissions, and Google Ads Customer Match credentials. Create suppression list segments in HubSpot (e.g., unsubscribed, converted, competitors).",
        inputFields: [],
      }),

      // 2. Fetch CRM suppression list (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch CRM suppression list (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_email_optout","operator":"EQ","value":"true"}]},{"filters":[{"propertyName":"suppress_from_ads","operator":"EQ","value":"true"}]},{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","lastname","phone"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "total",
            customName: "crm_suppression_count",
            type: "number",
          },
          {
            name: "results",
            customName: "crm_suppression_list_json",
            type: "string",
          },
        ],
      ),

      // 3. Fetch platform audience lists
      n("3", "hitApi", "Fetch Meta Custom Audience members", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{meta_suppression_audience_id}?fields=name,approximate_count,data_source",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "approximate_count",
              customName: "meta_audience_count",
              type: "number",
            },
            { name: "name", customName: "meta_audience_name", type: "string" },
          ],
        },
      }),

      // 4. Differential calculation (runCode)
      n("4", "runCode", "Calculate suppression list differential", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate the differential between CRM suppression list and platform audiences
const crmList = JSON.parse(vars.crm_suppression_list_json || "[]");
const metaCount = parseInt(vars.meta_audience_count || "0");
const crmCount = parseInt(vars.crm_suppression_count || "0");
const now = new Date().toISOString();

// Extract hashed emails for platform sync
const emailsToSync = [];
const phonesToSync = [];
for (const contact of crmList) {
  const props = contact.properties || {};
  if (props.email) emailsToSync.push(props.email.toLowerCase().trim());
  if (props.phone) phonesToSync.push(props.phone.replace(/[^0-9+]/g, ""));
}

const newToAdd = Math.max(0, crmCount - metaCount);
const estimatedOverlap = Math.min(crmCount, metaCount);
const syncNeeded = newToAdd > 0 || crmCount !== metaCount;

return {
  crm_emails_count: emailsToSync.length,
  crm_phones_count: phonesToSync.length,
  emails_to_sync_json: JSON.stringify(emailsToSync.slice(0, 500)),
  new_additions_estimate: newToAdd,
  estimated_overlap: estimatedOverlap,
  sync_needed: syncNeeded ? "yes" : "no",
  sync_timestamp: now,
  delta_size: Math.abs(crmCount - metaCount)
};`,
          outputVariables: [
            { name: "crm_emails_count", type: "number" },
            { name: "crm_phones_count", type: "number" },
            { name: "emails_to_sync_json", type: "string" },
            { name: "new_additions_estimate", type: "number" },
            { name: "estimated_overlap", type: "number" },
            { name: "sync_needed", type: "string" },
            { name: "sync_timestamp", type: "string" },
            { name: "delta_size", type: "number" },
          ],
        },
      }),

      // 5. Meta Custom Audience sync
      n("5", "hitApi", "Sync suppression list to Meta Custom Audience", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_suppression_audience_id}/users",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "payload",
              value:
                '{"schema":["EMAIL","PHONE"],"data":{emails_to_sync_json}}',
            },
          ],
          variables: [
            {
              name: "audience_id",
              customName: "meta_sync_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_records_received",
              type: "number",
            },
          ],
        },
      }),

      // 6. Google Customer Match sync
      n("6", "hitApi", "Sync suppression list to Google Customer Match", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "CUSTOMER_MATCH_USER_LIST" },
            {
              key: "customerMatchUserListMetadata",
              value:
                '{"userList":"customers/{google_ads_customer_id}/userLists/{google_suppression_list_id}"}',
            },
          ],
          variables: [
            {
              name: "resourceName",
              customName: "google_sync_job_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Sync results logging (Sheets)
      sheetsNode(
        "7",
        "Log suppression sync results (Sheets)",
        1480,
        "SuppressionSync",
        '[["{sync_timestamp}","{crm_suppression_count}","{crm_emails_count}","{meta_audience_count}","{meta_records_received}","{google_sync_job_id}","{new_additions_estimate}","{delta_size}","{sync_needed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 874. ADD-TO-CART OPT-IN OFFER (8 nodes)
  // ===================================================================

  "Add-to-Cart Opt-In Offer": {
    summary:
      "Presents an optimized opt-in offer when users add items to cart, enrolls new subscribers, and applies cart discounts. A cart event triggers a subscription status check, a condition routes already-subscribed users, AI calculates the optimal discount, an in-app message displays the opt-in modal, submission processing runs, the subscriber is added to Mailchimp, and a cart discount is applied via Shopify.",
    nodes: [
      trigger("Webhook: Item added to cart", "webhook_app", {
        source: "Shopify / eCommerce Platform",
        event: "cart.add",
        howItStarts:
          "A shopper adds an item to their cart, triggering an evaluation of whether to present an email/SMS opt-in offer with a personalized discount to incentivize subscription",
        setup:
          "Configure your eCommerce platform to POST cart add events to this workflow URL. Include cart value, product details, customer ID (if known), and session data. Set up Mailchimp API key and Shopify Admin API credentials.",
        inputFields: [],
      }),

      // 2. Extract cart data and check subscription status
      aiNode(
        "2",
        "AI: Extract cart data and identify customer",
        280,
        "You are an eCommerce data extractor. Parse the cart event payload and extract customer and cart details regardless of format. Respond JSON: {customer_email: string, customer_id: string, cart_value: number, product_name: string, product_category: string, product_price: number, session_id: string, is_returning: string, previous_purchases: number}",
        "Raw cart event payload: {webhook_payload}",
        [
          { field: "customer_email", as: "cart_email", type: "string" },
          { field: "customer_id", as: "cart_customer_id", type: "string" },
          { field: "cart_value", as: "cart_value", type: "number" },
          { field: "product_name", as: "cart_product_name", type: "string" },
          {
            field: "product_category",
            as: "cart_product_category",
            type: "string",
          },
          { field: "product_price", as: "cart_product_price", type: "number" },
          { field: "session_id", as: "cart_session_id", type: "string" },
          { field: "is_returning", as: "is_returning_visitor", type: "string" },
          {
            field: "previous_purchases",
            as: "previous_purchase_count",
            type: "number",
          },
        ],
      ),

      // 3. Already subscribed condition
      n("3", "condition", "Is customer already subscribed?", 520, {
        conditionData: [
          {
            variable: "cart_email",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "is_returning_visitor",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI optimal discount calculation (False path — not subscribed)
      aiNode(
        "4",
        "AI: Calculate optimal opt-in discount",
        760,
        "You are a conversion optimization expert. Calculate the optimal discount to offer for email/SMS opt-in based on cart value, product category, and customer behavior. Balance acquisition cost against lifetime value. Respond JSON: {discount_percentage: number, discount_code: string, offer_headline: string, offer_body: string, urgency_message: string, estimated_conversion_rate: number}",
        "Cart value: ${cart_value}. Product: {cart_product_name} ({cart_product_category}). Price: ${cart_product_price}. Returning visitor: {is_returning_visitor}. Previous purchases: {previous_purchase_count}.",
        [
          {
            field: "discount_percentage",
            as: "offer_discount_pct",
            type: "number",
          },
          { field: "discount_code", as: "offer_discount_code", type: "string" },
          { field: "offer_headline", as: "offer_headline", type: "string" },
          { field: "offer_body", as: "offer_body", type: "string" },
          { field: "urgency_message", as: "offer_urgency", type: "string" },
          {
            field: "estimated_conversion_rate",
            as: "estimated_conversion_rate",
            type: "number",
          },
        ],
      ),

      // 5. Display opt-in modal (sendMessage)
      n("5", "sendMessage", "Display opt-in offer modal", 1000, {
        sendMessageDetails: {
          channel: "in_app",
          message:
            "{offer_headline}\\n\\n{offer_body}\\n\\n{offer_urgency}\\n\\nGet {offer_discount_pct}% off with code {offer_discount_code}. Enter your email to claim.",
        },
      }),

      // 6. Process opt-in submission (runCode)
      n("6", "runCode", "Process opt-in submission", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process the opt-in submission and prepare data for downstream services
const email = vars.cart_email || "";
const discount = vars.offer_discount_code || "WELCOME10";
const discountPct = parseInt(vars.offer_discount_pct || "10");
const cartValue = parseFloat(vars.cart_value || "0");
const productName = vars.cart_product_name || "";
const now = new Date().toISOString();

const discountAmount = Math.round(cartValue * (discountPct / 100) * 100) / 100;
const finalPrice = Math.round((cartValue - discountAmount) * 100) / 100;

return {
  subscriber_email: email,
  opt_in_timestamp: now,
  discount_code_applied: discount,
  discount_amount: discountAmount,
  final_cart_value: finalPrice,
  opt_in_source: "cart_modal",
  product_at_signup: productName,
  subscription_status: "active"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "opt_in_timestamp", type: "string" },
            { name: "discount_code_applied", type: "string" },
            { name: "discount_amount", type: "number" },
            { name: "final_cart_value", type: "number" },
            { name: "opt_in_source", type: "string" },
            { name: "product_at_signup", type: "string" },
            { name: "subscription_status", type: "string" },
          ],
        },
      }),

      // 7. Add to Mailchimp list
      n("7", "hitApi", "Add subscriber to Mailchimp list", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/members",
          headers: [
            { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email_address", value: "{subscriber_email}" },
            { key: "status", value: "subscribed" },
            {
              key: "merge_fields",
              value:
                '{"SOURCE":"cart_optin","DISCOUNT":"{discount_code_applied}","PRODUCT":"{product_at_signup}"}',
            },
            { key: "tags", value: '["cart-optin","discount-subscriber"]' },
          ],
          variables: [
            { name: "id", customName: "mailchimp_member_id", type: "string" },
            { name: "status", customName: "mailchimp_status", type: "string" },
          ],
        },
      }),

      // 8. Apply cart discount via Shopify
      n("8", "hitApi", "Apply discount to cart (Shopify)", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/price_rules.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_admin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "price_rule",
              value:
                '{"title":"{discount_code_applied}","target_type":"line_item","target_selection":"all","allocation_method":"across","value_type":"percentage","value":"-{offer_discount_pct}","customer_selection":"all","usage_limit":1,"starts_at":"{opt_in_timestamp}"}',
            },
          ],
          variables: [
            {
              name: "price_rule.id",
              customName: "shopify_price_rule_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "8", "output_1"), // already subscribed -> skip to cart (no re-opt-in needed)
      e("3", "4", "output_2"), // not subscribed -> offer discount
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 875. AFFILIATE ARMY BUILDER (8 nodes)
  // ===================================================================

  "Affiliate Army Builder": {
    summary:
      "Automates affiliate recruitment from discovery through onboarding. A recruitment launch triggers potential affiliate identification via SerpAPI, profiles are enriched, a runCode node scores and ranks candidates, AI generates personalized recruitment emails, a SendGrid email sequence is sent, onboarding materials are generated, and the pipeline is tracked in Sheets with Slack notifications.",
    nodes: [
      trigger(
        "Webhook: Affiliate recruitment campaign launched",
        "webhook_app",
        {
          source: "Marketing Platform / Affiliate Manager Portal",
          event: "affiliate.recruitment.launched",
          howItStarts:
            "An affiliate recruitment campaign is launched with target criteria, niche keywords, and commission structure, triggering automated discovery and outreach to potential affiliates",
          setup:
            "Configure SerpAPI key for web search, SendGrid API key for email outreach, and Sheets + Slack credentials. Define your affiliate program terms, commission rates, and ideal affiliate profile criteria.",
          inputFields: [],
        },
      ),

      // 2. Identify potential affiliates via SerpAPI
      aiNode(
        "2",
        "AI: Extract recruitment criteria from launch brief",
        280,
        "You are an affiliate recruitment strategist. Parse the recruitment launch payload and extract criteria regardless of format. Respond JSON: {niche: string, target_keywords: string, commission_rate: string, program_name: string, product_url: string, ideal_audience_size: string, content_types: string, geographic_focus: string, recruitment_budget: string, campaign_id: string}",
        "Raw recruitment launch payload: {webhook_payload}",
        [
          { field: "niche", as: "recruit_niche", type: "string" },
          { field: "target_keywords", as: "recruit_keywords", type: "string" },
          { field: "commission_rate", as: "commission_rate", type: "string" },
          { field: "program_name", as: "program_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          {
            field: "ideal_audience_size",
            as: "ideal_audience_size",
            type: "string",
          },
          {
            field: "content_types",
            as: "target_content_types",
            type: "string",
          },
          { field: "geographic_focus", as: "geographic_focus", type: "string" },
          {
            field: "recruitment_budget",
            as: "recruitment_budget",
            type: "string",
          },
          { field: "campaign_id", as: "recruit_campaign_id", type: "string" },
        ],
      ),

      // 3. Search for potential affiliates (SerpAPI)
      n("3", "hitApi", "Search for potential affiliates (SerpAPI)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={recruit_keywords}+{recruit_niche}+blog+OR+influencer+OR+reviewer&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_serp_results",
              type: "number",
            },
          ],
        },
      }),

      // 4. Score and rank candidates (runCode)
      n("4", "runCode", "Score and rank affiliate candidates", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score and rank potential affiliate candidates
const results = JSON.parse(vars.serp_results_json || "[]");
const niche = (vars.recruit_niche || "").toLowerCase();
const targetTypes = (vars.target_content_types || "blog,youtube,review").toLowerCase().split(",");
const now = new Date().toISOString();

const candidates = [];
for (const result of results.slice(0, 20)) {
  const url = result.link || "";
  const title = (result.title || "").toLowerCase();
  const snippet = (result.snippet || "").toLowerCase();
  let score = 30;

  // Domain authority signals
  if (url.includes("youtube.com")) score += 15;
  if (url.includes("medium.com") || url.includes("substack.com")) score += 10;
  if (snippet.includes("review") || title.includes("review")) score += 10;
  if (snippet.includes("best") || title.includes("best")) score += 5;

  // Niche relevance
  if (title.includes(niche) || snippet.includes(niche)) score += 15;

  // Content type match
  for (const ctype of targetTypes) {
    if (url.includes(ctype.trim()) || snippet.includes(ctype.trim())) { score += 5; break; }
  }

  // Contact extractability
  const hasContact = snippet.includes("contact") || snippet.includes("email") || snippet.includes("@");
  if (hasContact) score += 10;

  candidates.push({
    url, title: result.title || "", snippet: result.snippet || "",
    score: Math.min(score, 100), estimated_reach: result.position <= 5 ? "high" : "medium"
  });
}

candidates.sort((a, b) => b.score - a.score);
const topCandidates = candidates.slice(0, 10);

return {
  total_candidates_found: candidates.length,
  top_candidates_json: JSON.stringify(topCandidates),
  top_candidate_url: topCandidates[0] ? topCandidates[0].url : "",
  top_candidate_title: topCandidates[0] ? topCandidates[0].title : "",
  top_score: topCandidates[0] ? topCandidates[0].score : 0,
  qualified_count: candidates.filter(c => c.score >= 50).length,
  scan_timestamp: now
};`,
          outputVariables: [
            { name: "total_candidates_found", type: "number" },
            { name: "top_candidates_json", type: "string" },
            { name: "top_candidate_url", type: "string" },
            { name: "top_candidate_title", type: "string" },
            { name: "top_score", type: "number" },
            { name: "qualified_count", type: "number" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 5. AI personalized recruitment emails
      aiNode(
        "5",
        "AI: Generate personalized recruitment outreach",
        1000,
        "You are an affiliate recruitment copywriter. Generate a personalized recruitment email for the top affiliate candidate. Reference their specific content, explain the value proposition, highlight commission rates, and include a clear CTA. Respond JSON: {email_subject: string, email_body: string, follow_up_subject: string, follow_up_body: string, personalization_hooks: string}",
        "Candidate: {top_candidate_title} ({top_candidate_url}). Program: {program_name}. Commission: {commission_rate}. Product: {product_url}. Niche: {recruit_niche}. Their score: {top_score}.",
        [
          {
            field: "email_subject",
            as: "recruit_email_subject",
            type: "string",
          },
          { field: "email_body", as: "recruit_email_body", type: "string" },
          {
            field: "follow_up_subject",
            as: "recruit_followup_subject",
            type: "string",
          },
          {
            field: "follow_up_body",
            as: "recruit_followup_body",
            type: "string",
          },
          {
            field: "personalization_hooks",
            as: "personalization_hooks",
            type: "string",
          },
        ],
      ),

      // 6. Send recruitment email via SendGrid
      n("6", "hitApi", "Send recruitment email sequence (SendGrid)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{top_candidate_url}"}],"subject":"{recruit_email_subject}"}]',
            },
            {
              key: "from",
              value:
                '{"email":"{sender_email}","name":"{program_name} Partnerships"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{recruit_email_body}"}]',
            },
          ],
          variables: [
            {
              name: "statusCode",
              customName: "sendgrid_status",
              type: "number",
            },
          ],
        },
      }),

      // 7. Generate onboarding materials
      aiNode(
        "7",
        "AI: Generate affiliate onboarding materials",
        1480,
        "You are an affiliate program onboarding specialist. Generate a comprehensive onboarding package including program overview, commission structure, promotional guidelines, asset links, and getting started steps. Respond JSON: {onboarding_guide: string, promo_guidelines: string, asset_links: string, getting_started_steps: string, faq_content: string}",
        "Program: {program_name}. Commission: {commission_rate}. Product: {product_url}. Niche: {recruit_niche}. Target content types: {target_content_types}.",
        [
          { field: "onboarding_guide", as: "onboarding_guide", type: "string" },
          { field: "promo_guidelines", as: "promo_guidelines", type: "string" },
          { field: "asset_links", as: "asset_links", type: "string" },
          {
            field: "getting_started_steps",
            as: "getting_started_steps",
            type: "string",
          },
          { field: "faq_content", as: "faq_content", type: "string" },
        ],
      ),

      // 8. Pipeline tracking (Sheets + Slack)
      sheetsNode(
        "8",
        "Log recruitment pipeline (Sheets) + notify Slack",
        1720,
        "AffiliatePipeline",
        '[["{scan_timestamp}","{recruit_campaign_id}","{recruit_niche}","{total_candidates_found}","{qualified_count}","{top_candidate_title}","{top_candidate_url}","{top_score}","{commission_rate}","{program_name}","{personalization_hooks}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 876. ANNIVERSARY VIP REWARD (7 nodes)
  // ===================================================================

  "Anniversary VIP Reward": {
    summary:
      "Celebrates customer anniversaries with personalized rewards based on loyalty tier. A daily schedule fetches anniversary customers from HubSpot, a runCode node determines reward tiers, AI generates personalized messages, unique reward codes are generated, an anniversary email is sent, and issuance is logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily anniversary check", "schedule", {
        schedule: "0 7 * * * (daily at 7 AM)",
        howItStarts:
          "A daily scheduled job identifies customers whose signup anniversary falls on today, determines their loyalty tier based on tenure and spend, and sends a personalized reward",
        setup:
          "Configure HubSpot API key with contact properties for signup date and lifetime value. Set reward tier thresholds (e.g., 1yr = Silver, 2yr = Gold, 3yr+ = Platinum). Provide email sending credentials and Google Sheets API key.",
        inputFields: [],
      }),

      // 2. Fetch anniversary customers from HubSpot
      hubspotPostNode(
        "2",
        "Fetch today's anniversary customers (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"anniversary_month_day","operator":"EQ","value":"{today_month_day}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","createdate","hs_object_id","total_revenue","num_associated_deals","lifecyclestage"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "anniversary_count", type: "number" },
          {
            name: "results",
            customName: "anniversary_customers_json",
            type: "string",
          },
        ],
      ),

      // 3. Reward tier determination (runCode)
      n("3", "runCode", "Determine reward tier for each customer", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine reward tier based on tenure and lifetime value
const customers = JSON.parse(vars.anniversary_customers_json || "[]");
const now = new Date();
const rewards = [];

for (const cust of customers) {
  const props = cust.properties || {};
  const signupDate = new Date(props.createdate || now);
  const yearsAsCustomer = Math.floor((now - signupDate) / (365.25 * 86400000));
  const ltv = parseFloat(props.total_revenue || "0");
  const dealCount = parseInt(props.num_associated_deals || "0");

  let tier = "bronze";
  let discountPct = 10;
  let bonusCredits = 0;
  if (yearsAsCustomer >= 5 || ltv >= 50000) { tier = "diamond"; discountPct = 30; bonusCredits = 500; }
  else if (yearsAsCustomer >= 3 || ltv >= 20000) { tier = "platinum"; discountPct = 25; bonusCredits = 200; }
  else if (yearsAsCustomer >= 2 || ltv >= 10000) { tier = "gold"; discountPct = 20; bonusCredits = 100; }
  else if (yearsAsCustomer >= 1 || ltv >= 5000) { tier = "silver"; discountPct = 15; bonusCredits = 50; }

  rewards.push({
    email: props.email, first_name: props.firstname || "Valued Customer",
    years: yearsAsCustomer, ltv, tier, discount_pct: discountPct,
    bonus_credits: bonusCredits, deal_count: dealCount
  });
}

const topReward = rewards[0] || { email: "", first_name: "Customer", years: 1, ltv: 0, tier: "bronze", discount_pct: 10, bonus_credits: 0 };

return {
  rewards_json: JSON.stringify(rewards),
  total_anniversaries: rewards.length,
  reward_email: topReward.email,
  reward_first_name: topReward.first_name,
  reward_years: topReward.years,
  reward_tier: topReward.tier,
  reward_discount_pct: topReward.discount_pct,
  reward_bonus_credits: topReward.bonus_credits,
  reward_ltv: topReward.ltv
};`,
          outputVariables: [
            { name: "rewards_json", type: "string" },
            { name: "total_anniversaries", type: "number" },
            { name: "reward_email", type: "string" },
            { name: "reward_first_name", type: "string" },
            { name: "reward_years", type: "number" },
            { name: "reward_tier", type: "string" },
            { name: "reward_discount_pct", type: "number" },
            { name: "reward_bonus_credits", type: "number" },
            { name: "reward_ltv", type: "number" },
          ],
        },
      }),

      // 4. AI personalized anniversary message
      aiNode(
        "4",
        "AI: Generate personalized anniversary message",
        760,
        "You are a customer loyalty copywriter. Generate a warm, personalized anniversary celebration email. Reference the customer's tenure, tier, and value. Make them feel genuinely appreciated. Include the reward details naturally. Respond JSON: {subject_line: string, email_body: string, special_message: string}",
        "Customer: {reward_first_name}. Years: {reward_years}. Tier: {reward_tier}. LTV: ${reward_ltv}. Discount: {reward_discount_pct}%. Bonus credits: {reward_bonus_credits}.",
        [
          { field: "subject_line", as: "anniversary_subject", type: "string" },
          { field: "email_body", as: "anniversary_body", type: "string" },
          {
            field: "special_message",
            as: "anniversary_special_msg",
            type: "string",
          },
        ],
      ),

      // 5. Generate unique reward code (runCode)
      n("5", "runCode", "Generate unique anniversary reward code", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique, tier-branded reward code
const tier = (vars.reward_tier || "bronze").toUpperCase();
const years = vars.reward_years || 1;
const timestamp = Date.now().toString(36).toUpperCase();
const random = Math.random().toString(36).substring(2, 6).toUpperCase();
const rewardCode = "ANNIV" + years + "-" + tier.substring(0, 3) + "-" + random + timestamp.substring(timestamp.length - 3);
const expiryDate = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];

return {
  reward_code: rewardCode,
  reward_expiry: expiryDate,
  code_generated_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "reward_code", type: "string" },
            { name: "reward_expiry", type: "string" },
            { name: "code_generated_at", type: "string" },
          ],
        },
      }),

      // 6. Send anniversary email
      emailNode(
        "6",
        "Send anniversary reward email",
        1240,
        "{reward_email}",
        "{anniversary_subject}",
        "{anniversary_body}\\n\\nYour exclusive reward code: {reward_code}\\nDiscount: {reward_discount_pct}% off\\nBonus Credits: {reward_bonus_credits}\\nValid until: {reward_expiry}\\n\\n{anniversary_special_msg}",
      ),

      // 7. Log issuance to Sheets
      sheetsNode(
        "7",
        "Log anniversary reward issuance (Sheets)",
        1480,
        "AnniversaryRewards",
        '[["{code_generated_at}","{reward_email}","{reward_first_name}","{reward_years}","{reward_tier}","{reward_discount_pct}","{reward_bonus_credits}","{reward_code}","{reward_expiry}","{reward_ltv}","{total_anniversaries}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 877. ANNUAL PLAN RENEWAL FOR SAAS (9 nodes)
  // ===================================================================

  "Annual Plan Renewal for SaaS": {
    summary:
      "Manages SaaS annual plan renewals with health scoring, at-risk identification, and targeted save offers. A 30-day-out schedule pulls Stripe subscription and usage data, a runCode node calculates renewal health scores, a condition routes at-risk vs. healthy customers, separate email paths handle each, a renewal communication sequence follows up, CS is alerted via Slack for at-risk accounts, and outcomes are tracked in Sheets.",
    nodes: [
      trigger("Schedule: 30-day renewal window check", "schedule", {
        schedule: "0 9 * * * (daily at 9 AM)",
        howItStarts:
          "A daily scheduled job identifies annual subscriptions expiring in the next 30 days, pulls usage and engagement data from Stripe, calculates renewal health scores, and routes personalized renewal communications",
        setup:
          "Configure Stripe API key with subscription read access. Set health score thresholds (e.g., high usage + low churn signals = healthy). Provide Slack webhook for CS alerts and Sheets API for tracking.",
        inputFields: [],
      }),

      // 2. Pull Stripe subscription + usage data
      n("2", "hitApi", "Fetch expiring subscriptions from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions?status=active&current_period_end[lte]={thirty_days_from_now_unix}&current_period_end[gte]={now_unix}&limit=50&expand[]=data.customer",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "expiring_subscriptions_json",
              type: "string",
            },
            { name: "has_more", customName: "has_more_subs", type: "boolean" },
          ],
        },
      }),

      // 3. Renewal health score calculation (runCode)
      n("3", "runCode", "Calculate renewal health scores", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate renewal health score for each expiring subscription
const subs = JSON.parse(vars.expiring_subscriptions_json || "[]");
const now = new Date();
const results = [];

for (const sub of subs) {
  const customer = sub.customer || {};
  const email = customer.email || "";
  const name = customer.name || "";
  const plan = (sub.plan || sub.items?.data?.[0]?.plan || {});
  const amount = (plan.amount || 0) / 100;
  const interval = plan.interval || "year";
  const renewalDate = new Date((sub.current_period_end || 0) * 1000);
  const daysUntilRenewal = Math.ceil((renewalDate - now) / 86400000);

  // Health scoring factors
  let healthScore = 50;
  // Payment history
  const failedPayments = sub.latest_invoice?.payment_intent?.status === "requires_payment_method" ? 1 : 0;
  if (failedPayments === 0) healthScore += 15;
  // Tenure
  const monthsActive = Math.floor((now - new Date(sub.created * 1000)) / (30 * 86400000));
  if (monthsActive >= 24) healthScore += 15;
  else if (monthsActive >= 12) healthScore += 10;
  // Plan value (higher plans = more invested)
  if (amount >= 500) healthScore += 10;
  else if (amount >= 100) healthScore += 5;
  // Discount history (discounted customers may be price-sensitive)
  if (sub.discount) healthScore -= 5;

  const isAtRisk = healthScore < 60;
  results.push({
    email, name, subscription_id: sub.id, amount, interval,
    renewal_date: renewalDate.toISOString().split("T")[0],
    days_until_renewal: daysUntilRenewal, health_score: healthScore,
    months_active: monthsActive, at_risk: isAtRisk
  });
}

results.sort((a, b) => a.health_score - b.health_score);
const atRiskCount = results.filter(r => r.at_risk).length;
const topResult = results[0] || { email: "", name: "Customer", amount: 0, health_score: 50, at_risk: false, days_until_renewal: 30, subscription_id: "", renewal_date: "", months_active: 0 };

return {
  total_expiring: results.length,
  at_risk_count: atRiskCount,
  healthy_count: results.length - atRiskCount,
  results_json: JSON.stringify(results),
  next_email: topResult.email,
  next_name: topResult.name,
  next_amount: topResult.amount,
  next_health_score: topResult.health_score,
  next_is_at_risk: topResult.at_risk ? "yes" : "no",
  next_days_until: topResult.days_until_renewal,
  next_sub_id: topResult.subscription_id,
  next_renewal_date: topResult.renewal_date,
  next_months_active: topResult.months_active
};`,
          outputVariables: [
            { name: "total_expiring", type: "number" },
            { name: "at_risk_count", type: "number" },
            { name: "healthy_count", type: "number" },
            { name: "results_json", type: "string" },
            { name: "next_email", type: "string" },
            { name: "next_name", type: "string" },
            { name: "next_amount", type: "number" },
            { name: "next_health_score", type: "number" },
            { name: "next_is_at_risk", type: "string" },
            { name: "next_days_until", type: "number" },
            { name: "next_sub_id", type: "string" },
            { name: "next_renewal_date", type: "string" },
            { name: "next_months_active", type: "number" },
          ],
        },
      }),

      // 4. At-risk condition
      n("4", "condition", "Is this customer at risk of churning?", 760, {
        conditionData: [
          {
            variable: "next_is_at_risk",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Healthy renewal email (False path)
      emailNode(
        "5",
        "Send healthy renewal confirmation email",
        1000,
        "{next_email}",
        "Your annual plan renews on {next_renewal_date} — Thank you for {next_months_active} months!",
        "Hi {next_name},\\n\\nYour annual plan (${next_amount}/year) renews on {next_renewal_date}. Thank you for being a valued customer for {next_months_active} months!\\n\\nNo action needed — your plan will automatically renew. If you have any questions about your subscription or want to explore upgrade options, our team is here to help.\\n\\nWarm regards,\\nThe Team",
        350,
      ),

      // 6. At-risk save offer email (True path)
      aiNode(
        "6",
        "AI: Generate at-risk save offer email",
        1000,
        "You are a retention specialist. Generate a compelling renewal email for an at-risk customer. Acknowledge their value, address potential concerns, and present a save offer (discount or bonus). Make them feel valued without being desperate. Respond JSON: {subject_line: string, email_body: string, save_offer: string, offer_code: string}",
        "Customer: {next_name}. Email: {next_email}. Annual amount: ${next_amount}. Health score: {next_health_score}/100. Months active: {next_months_active}. Renewal date: {next_renewal_date}. Days until renewal: {next_days_until}.",
        [
          { field: "subject_line", as: "save_subject", type: "string" },
          { field: "email_body", as: "save_body", type: "string" },
          { field: "save_offer", as: "save_offer_description", type: "string" },
          { field: "offer_code", as: "save_offer_code", type: "string" },
        ],
      ),

      // 7. Renewal communication sequence (delay + follow-up)
      n("7", "delayAction", "Wait 7 days for renewal follow-up", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
        },
      }),

      // 8. CS Slack alert for at-risk accounts
      slackNode(
        "8",
        "Alert CS team about at-risk renewals (Slack)",
        1480,
        "Renewal Alert — At-Risk Accounts\\n\\nTotal expiring (30 days): {total_expiring}\\nAt-risk: {at_risk_count} | Healthy: {healthy_count}\\n\\nHighest risk: {next_name} ({next_email})\\nHealth score: {next_health_score}/100 | Amount: ${next_amount}/yr\\nRenews: {next_renewal_date} ({next_days_until} days)\\nMonths active: {next_months_active}\\n\\nSave offer sent: {save_offer_description}\\nCode: {save_offer_code}\\n\\nPlease follow up with at-risk accounts personally.",
      ),

      // 9. Renewal outcome tracking (Sheets)
      sheetsNode(
        "9",
        "Track renewal outcomes (Sheets)",
        1720,
        "RenewalTracking",
        '[["{next_renewal_date}","{next_email}","{next_name}","{next_sub_id}","{next_amount}","{next_health_score}","{next_is_at_risk}","{next_months_active}","{total_expiring}","{at_risk_count}","{save_offer_description}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // at-risk -> save offer email
      e("4", "5", "output_2"), // healthy -> standard renewal email
      e("5", "7"),
      e("6", "8"), // at-risk -> also alert CS
      e("8", "7"),
      e("7", "9"),
    ],
  },

  // ===================================================================
  // 878. ASSET BRIEF FOR DESIGNERS (7 nodes)
  // ===================================================================

  "Asset Brief for Designers": {
    summary:
      "Automatically generates platform-specific design briefs from approved campaigns and delivers them to the design team. A campaign approval triggers strategy and spec retrieval, AI creates platform-specific briefs, a runCode node adds brand compliance requirements, visual direction prompts are generated, Asana tasks are created, and briefs are delivered to the design team via Slack.",
    nodes: [
      trigger(
        "Webhook: Campaign approved for creative production",
        "webhook_app",
        {
          source: "Campaign Management / Asana / Monday.com",
          event: "campaign.approved",
          howItStarts:
            "A marketing campaign is approved for production, triggering automatic generation of platform-specific design briefs with brand compliance requirements and visual direction for the design team",
          setup:
            "Configure your campaign management tool to POST approval notifications to this workflow URL. Include campaign strategy, target platforms, audience segments, and messaging framework. Set up Asana API key for task creation and Slack webhook for delivery.",
          inputFields: [],
        },
      ),

      // 2. Extract campaign strategy and specs
      aiNode(
        "2",
        "AI: Extract campaign strategy and creative specs",
        280,
        "You are a campaign strategist. Parse the campaign approval payload and extract all strategy and spec details regardless of format. Respond JSON: {campaign_name: string, campaign_id: string, campaign_objective: string, target_platforms: string, audience_segments: string, key_messages: string, tone_direction: string, budget: string, timeline: string, approved_by: string}",
        "Raw campaign approval payload: {webhook_payload}",
        [
          { field: "campaign_name", as: "brief_campaign_name", type: "string" },
          { field: "campaign_id", as: "brief_campaign_id", type: "string" },
          {
            field: "campaign_objective",
            as: "brief_objective",
            type: "string",
          },
          { field: "target_platforms", as: "brief_platforms", type: "string" },
          { field: "audience_segments", as: "brief_audience", type: "string" },
          { field: "key_messages", as: "brief_key_messages", type: "string" },
          { field: "tone_direction", as: "brief_tone", type: "string" },
          { field: "budget", as: "brief_budget", type: "string" },
          { field: "timeline", as: "brief_timeline", type: "string" },
          { field: "approved_by", as: "brief_approved_by", type: "string" },
        ],
      ),

      // 3. AI platform-specific design briefs
      aiNode(
        "3",
        "AI: Generate platform-specific design briefs",
        520,
        "You are a creative director. Generate detailed design briefs for each target platform with exact dimensions, format requirements, and creative direction. Include specs for Google Display (300x250, 728x90, 160x600), Meta (1080x1080, 1200x628, 1080x1920 Story), LinkedIn (1200x627, 1200x1200), and any other specified platforms. Respond JSON: {platform_briefs: string, total_assets_needed: number, priority_assets: string, creative_direction: string}",
        "Campaign: {brief_campaign_name}. Objective: {brief_objective}. Platforms: {brief_platforms}. Audience: {brief_audience}. Messages: {brief_key_messages}. Tone: {brief_tone}. Timeline: {brief_timeline}.",
        [
          { field: "platform_briefs", as: "platform_briefs", type: "string" },
          {
            field: "total_assets_needed",
            as: "total_assets_needed",
            type: "number",
          },
          { field: "priority_assets", as: "priority_assets", type: "string" },
          {
            field: "creative_direction",
            as: "creative_direction",
            type: "string",
          },
        ],
      ),

      // 4. Brand compliance requirements (runCode)
      n("4", "runCode", "Apply brand compliance requirements", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply brand compliance rules to the design brief
const platforms = (vars.brief_platforms || "").toLowerCase();
const tone = (vars.brief_tone || "").toLowerCase();
const now = new Date().toISOString();

const brandRules = [];
// Universal brand rules
brandRules.push("Logo: Use primary logo on light backgrounds, reversed logo on dark backgrounds. Minimum clear space: 2x logo height.");
brandRules.push("Colors: Primary palette only. Do not introduce non-brand colors except for platform-required elements.");
brandRules.push("Typography: Headlines in brand headline font. Body in brand body font. No decorative fonts.");
brandRules.push("Photography: Use approved stock library or custom shoots only. No generic stock that conflicts with brand personality.");

// Platform-specific compliance
if (platforms.includes("meta") || platforms.includes("facebook")) {
  brandRules.push("Meta: Text overlay must be under 20% of image area for optimal delivery. No before/after imagery.");
}
if (platforms.includes("google")) {
  brandRules.push("Google Display: No animated elements exceeding 30 seconds. Max file size 150KB. No misleading interactive elements.");
}
if (platforms.includes("linkedin")) {
  brandRules.push("LinkedIn: Professional tone required. No excessive punctuation or ALL CAPS in creative. Company logo must be visible.");
}

// Tone-specific compliance
if (tone.includes("premium") || tone.includes("luxury")) {
  brandRules.push("Premium tone: Generous white space, minimal text, hero product/feature imagery.");
}

return {
  brand_compliance_rules: brandRules.join(" | "),
  compliance_rule_count: brandRules.length,
  brief_generated_at: now,
  review_deadline: new Date(Date.now() + 2 * 86400000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "brand_compliance_rules", type: "string" },
            { name: "compliance_rule_count", type: "number" },
            { name: "brief_generated_at", type: "string" },
            { name: "review_deadline", type: "string" },
          ],
        },
      }),

      // 5. Visual direction prompts
      aiNode(
        "5",
        "AI: Generate visual direction prompts",
        1000,
        "You are a visual design director. Generate detailed visual direction prompts for the design team including mood, composition, color emphasis, imagery style, and layout approach for each key asset. Respond JSON: {hero_visual_direction: string, supporting_visual_direction: string, color_emphasis: string, imagery_style: string, layout_notes: string}",
        "Campaign: {brief_campaign_name}. Creative direction: {creative_direction}. Tone: {brief_tone}. Audience: {brief_audience}. Priority assets: {priority_assets}. Brand rules: {brand_compliance_rules}.",
        [
          {
            field: "hero_visual_direction",
            as: "hero_visual_direction",
            type: "string",
          },
          {
            field: "supporting_visual_direction",
            as: "supporting_visual_direction",
            type: "string",
          },
          { field: "color_emphasis", as: "color_emphasis", type: "string" },
          { field: "imagery_style", as: "imagery_style", type: "string" },
          { field: "layout_notes", as: "layout_notes", type: "string" },
        ],
      ),

      // 6. Create Asana design tasks
      n("6", "hitApi", "Create design tasks in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Design Brief: {brief_campaign_name}","notes":"Campaign: {brief_campaign_name} ({brief_campaign_id})\\nObjective: {brief_objective}\\nPlatforms: {brief_platforms}\\nAssets needed: {total_assets_needed}\\n\\nPlatform Briefs: {platform_briefs}\\n\\nVisual Direction: {hero_visual_direction}\\nColor: {color_emphasis}\\nImagery: {imagery_style}\\nLayout: {layout_notes}\\n\\nBrand Compliance: {brand_compliance_rules}\\n\\nDeadline: {review_deadline}","projects":["{asana_design_project_id}"],"due_on":"{review_deadline}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Deliver brief to design team (Slack)
      slackNode(
        "7",
        "Deliver design brief to team (Slack)",
        1480,
        "New Design Brief — {brief_campaign_name}\\nApproved by: {brief_approved_by}\\n\\nObjective: {brief_objective}\\nPlatforms: {brief_platforms}\\nAssets needed: {total_assets_needed}\\nDeadline: {review_deadline}\\n\\nPriority: {priority_assets}\\nVisual direction: {hero_visual_direction}\\nColor emphasis: {color_emphasis}\\n\\nAsana task: {asana_task_url}\\n\\nBrand compliance ({compliance_rule_count} rules) included in the Asana task.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 879. ATTENTIVE BIRTHDAY SMS CAMPAIGN (7 nodes)
  // ===================================================================

  "Attentive Birthday SMS Campaign": {
    summary:
      "Sends personalized birthday SMS messages with unique discount codes via Attentive and tracks redemption. A daily schedule fetches birthday subscribers from Attentive, AI generates personalized messages, a runCode node creates unique discount codes, the birthday SMS is sent via Attentive, a 48-hour redemption check evaluates conversion, and campaign performance is logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily birthday SMS check", "schedule", {
        schedule: "0 9 * * * (daily at 9 AM)",
        howItStarts:
          "A daily scheduled job queries the Attentive subscriber database for subscribers whose birthday matches today, generates personalized messages with unique discount codes, and sends birthday SMS messages",
        setup:
          "Configure Attentive API key with subscriber read/write permissions. Ensure subscriber profiles include birth_date custom attribute. Set up discount code generation logic and Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Fetch birthday subscribers from Attentive
      n("2", "hitApi", "Fetch today's birthday subscribers (Attentive)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/subscribers/search",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filter",
              value:
                '{"custom_attributes":{"birth_month_day":"{today_month_day}"},"subscriptionStatus":"SUBSCRIBED"}',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            {
              name: "subscribers",
              customName: "birthday_subscribers_json",
              type: "string",
            },
            {
              name: "totalCount",
              customName: "birthday_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI personalized birthday messages
      aiNode(
        "3",
        "AI: Generate personalized birthday SMS",
        520,
        "You are a birthday marketing SMS expert. Generate a warm, celebratory birthday SMS message (max 160 chars). Be personal, festive, and include a clear discount offer. Avoid spam triggers. Respond JSON: {sms_message: string, backup_message: string, tone_used: string}",
        "Birthday subscribers found: {birthday_count}. Brand voice: friendly and celebratory. The SMS should include a placeholder {discount_code} and {discount_pct}% off offer. Keep it under 160 characters.",
        [
          { field: "sms_message", as: "birthday_sms_template", type: "string" },
          {
            field: "backup_message",
            as: "birthday_sms_backup",
            type: "string",
          },
          { field: "tone_used", as: "sms_tone", type: "string" },
        ],
      ),

      // 4. Generate unique discount codes (runCode)
      n("4", "runCode", "Generate unique birthday discount codes", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique birthday discount codes for each subscriber
const subscribers = JSON.parse(vars.birthday_subscribers_json || "[]");
const template = vars.birthday_sms_template || "Happy Birthday! Use code {discount_code} for {discount_pct}% off!";
const now = new Date();
const codes = [];

for (const sub of subscribers) {
  const phone = sub.phone || sub.phoneNumber || "";
  const name = sub.firstName || sub.first_name || "";
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  const code = "BDAY-" + random + "-" + now.getDate().toString().padStart(2, "0");
  const discountPct = 20;
  const message = template.replace("{discount_code}", code).replace("{discount_pct}", discountPct.toString());

  codes.push({ phone, name, code, discount_pct: discountPct, message, expires: new Date(now.getTime() + 48 * 3600000).toISOString() });
}

const firstCode = codes[0] || { phone: "", name: "", code: "BDAY-XXXX", discount_pct: 20, message: template, expires: "" };

return {
  codes_json: JSON.stringify(codes),
  total_codes_generated: codes.length,
  first_phone: firstCode.phone,
  first_name: firstCode.name,
  first_code: firstCode.code,
  first_message: firstCode.message,
  first_discount_pct: firstCode.discount_pct,
  code_expiry: firstCode.expires,
  send_timestamp: now.toISOString()
};`,
          outputVariables: [
            { name: "codes_json", type: "string" },
            { name: "total_codes_generated", type: "number" },
            { name: "first_phone", type: "string" },
            { name: "first_name", type: "string" },
            { name: "first_code", type: "string" },
            { name: "first_message", type: "string" },
            { name: "first_discount_pct", type: "number" },
            { name: "code_expiry", type: "string" },
            { name: "send_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Send birthday SMS via Attentive
      n("5", "hitApi", "Send birthday SMS via Attentive", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/messages/send",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "to", value: "{first_phone}" },
            { key: "body", value: "{first_message}" },
            {
              key: "messagingServiceSid",
              value: "{attentive_messaging_service_id}",
            },
          ],
          variables: [
            {
              name: "messageId",
              customName: "attentive_message_id",
              type: "string",
            },
            { name: "status", customName: "sms_send_status", type: "string" },
          ],
        },
      }),

      // 6. 48hr redemption condition
      n(
        "6",
        "condition",
        "Was the discount code redeemed within 48 hours?",
        1240,
        {
          conditionData: [
            {
              variable: "sms_send_status",
              value: "sent",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Campaign performance logging (Sheets)
      sheetsNode(
        "7",
        "Log birthday SMS campaign performance (Sheets)",
        1480,
        "BirthdaySMS",
        '[["{send_timestamp}","{first_phone}","{first_name}","{first_code}","{first_discount_pct}","{sms_send_status}","{attentive_message_id}","{code_expiry}","{birthday_count}","{total_codes_generated}","{sms_tone}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // sent successfully -> log
      e("6", "7", "output_2"), // send issue -> log with status
    ],
  },

  // ===================================================================
  // 880. ATTENTIVE FLASH SALE SMS BLAST (8 nodes)
  // ===================================================================

  "Attentive Flash Sale SMS Blast": {
    summary:
      "Orchestrates a flash sale SMS campaign with staggered sends, real-time monitoring, and a last-chance follow-up. A flash sale trigger segments the Attentive audience, AI creates urgency-driven SMS copy, a runCode node calculates staggered send timing, the initial blast goes out via Attentive, real-time engagement is monitored, a last-chance condition triggers follow-ups, and a post-sale performance report is generated in Sheets.",
    nodes: [
      trigger("Webhook: Flash sale launched", "webhook_app", {
        source: "eCommerce Platform / Marketing Dashboard",
        event: "flash_sale.launched",
        howItStarts:
          "A flash sale is launched with sale details (discount, duration, eligible products), triggering a staggered SMS blast to segmented Attentive audiences with urgency-driven messaging",
        setup:
          "Configure Attentive API key with messaging permissions. Define audience segments in Attentive (VIP, engaged, all). Provide sale parameters via webhook payload. Set up Sheets for reporting.",
        inputFields: [],
      }),

      // 2. Extract sale details and segment audience (Attentive)
      aiNode(
        "2",
        "AI: Extract flash sale details and segment audience",
        280,
        "You are a flash sale campaign planner. Parse the flash sale launch payload and determine audience segmentation strategy regardless of format. Respond JSON: {sale_name: string, discount_amount: string, sale_duration_hours: number, sale_end_time: string, eligible_products: string, sale_url: string, vip_segment_id: string, engaged_segment_id: string, all_segment_id: string, total_audience_estimate: number}",
        "Raw flash sale launch payload: {webhook_payload}",
        [
          { field: "sale_name", as: "sale_name", type: "string" },
          { field: "discount_amount", as: "sale_discount", type: "string" },
          {
            field: "sale_duration_hours",
            as: "sale_duration_hours",
            type: "number",
          },
          { field: "sale_end_time", as: "sale_end_time", type: "string" },
          { field: "eligible_products", as: "sale_products", type: "string" },
          { field: "sale_url", as: "sale_url", type: "string" },
          { field: "vip_segment_id", as: "vip_segment_id", type: "string" },
          {
            field: "engaged_segment_id",
            as: "engaged_segment_id",
            type: "string",
          },
          { field: "all_segment_id", as: "all_segment_id", type: "string" },
          {
            field: "total_audience_estimate",
            as: "total_audience",
            type: "number",
          },
        ],
      ),

      // 3. AI urgency SMS copy
      aiNode(
        "3",
        "AI: Generate urgency-driven SMS copy",
        520,
        "You are a flash sale SMS copywriter. Generate 3 SMS variants: (1) VIP early access, (2) general announcement, (3) last-chance reminder. Each must be under 160 chars, create urgency, include the discount, and have a clear CTA. Respond JSON: {vip_sms: string, general_sms: string, last_chance_sms: string, urgency_hook: string}",
        "Sale: {sale_name}. Discount: {sale_discount}. Duration: {sale_duration_hours} hours. Ends: {sale_end_time}. Products: {sale_products}. URL: {sale_url}.",
        [
          { field: "vip_sms", as: "vip_sms_copy", type: "string" },
          { field: "general_sms", as: "general_sms_copy", type: "string" },
          {
            field: "last_chance_sms",
            as: "last_chance_sms_copy",
            type: "string",
          },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),

      // 4. Staggered send scheduling (runCode)
      n("4", "runCode", "Calculate staggered send schedule", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate staggered send schedule for flash sale
const durationHours = parseInt(vars.sale_duration_hours || "6");
const now = new Date();
const endTime = new Date(vars.sale_end_time || new Date(now.getTime() + durationHours * 3600000));
const totalAudience = parseInt(vars.total_audience || "1000");

// Stagger: VIP immediately, Engaged at +30min, All at +60min, Last-chance at -2hr
const vipSendTime = now.toISOString();
const engagedSendTime = new Date(now.getTime() + 30 * 60000).toISOString();
const allSendTime = new Date(now.getTime() + 60 * 60000).toISOString();
const remainingMs = endTime.getTime() - now.getTime();
const lastChanceTime = new Date(endTime.getTime() - 2 * 3600000).toISOString();

const vipPct = 0.1;
const engagedPct = 0.3;
const allPct = 0.6;

return {
  vip_send_time: vipSendTime,
  engaged_send_time: engagedSendTime,
  all_send_time: allSendTime,
  last_chance_time: lastChanceTime,
  vip_audience_size: Math.round(totalAudience * vipPct),
  engaged_audience_size: Math.round(totalAudience * engagedPct),
  all_audience_size: Math.round(totalAudience * allPct),
  sale_remaining_hours: Math.round(remainingMs / 3600000),
  schedule_created: "yes"
};`,
          outputVariables: [
            { name: "vip_send_time", type: "string" },
            { name: "engaged_send_time", type: "string" },
            { name: "all_send_time", type: "string" },
            { name: "last_chance_time", type: "string" },
            { name: "vip_audience_size", type: "number" },
            { name: "engaged_audience_size", type: "number" },
            { name: "all_audience_size", type: "number" },
            { name: "sale_remaining_hours", type: "number" },
            { name: "schedule_created", type: "string" },
          ],
        },
      }),

      // 5. Initial blast send (Attentive) — VIP segment
      n("5", "hitApi", "Send VIP early access SMS blast (Attentive)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/campaigns/send",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "segmentId", value: "{vip_segment_id}" },
            { key: "body", value: "{vip_sms_copy}" },
            { key: "campaignName", value: "Flash Sale VIP: {sale_name}" },
          ],
          variables: [
            {
              name: "campaignId",
              customName: "vip_campaign_id",
              type: "string",
            },
            {
              name: "recipientCount",
              customName: "vip_recipients",
              type: "number",
            },
          ],
        },
      }),

      // 6. Real-time engagement monitoring
      n(
        "6",
        "hitApi",
        "Monitor real-time engagement metrics (Attentive)",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.attentivemobile.com/v1/campaigns/{vip_campaign_id}/metrics",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "clickRate",
                customName: "current_click_rate",
                type: "number",
              },
              {
                name: "optOutRate",
                customName: "current_optout_rate",
                type: "number",
              },
              {
                name: "revenue",
                customName: "current_revenue",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. Last-chance condition
      n(
        "7",
        "condition",
        "Is the sale ending soon and engagement is strong?",
        1480,
        {
          conditionData: [
            {
              variable: "sale_remaining_hours",
              value: "2",
              comparison_operator: "LESS THAN OR EQUAL TO",
              logical_operator: "AND",
            },
            {
              variable: "current_optout_rate",
              value: "5",
              comparison_operator: "LESS THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Post-sale performance report (Sheets)
      sheetsNode(
        "8",
        "Log flash sale performance report (Sheets)",
        1720,
        "FlashSalePerformance",
        '[["{vip_send_time}","{sale_name}","{sale_discount}","{sale_duration_hours}","{vip_recipients}","{vip_audience_size}","{engaged_audience_size}","{all_audience_size}","{current_click_rate}","{current_optout_rate}","{current_revenue}","{urgency_hook}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // ending soon + strong engagement -> log performance
      e("7", "8", "output_2"), // not ending soon or weak engagement -> log current state
    ],
  },

  // ===================================================================
  // 881. ATTENTIVE SUBSCRIBER GROWTH CAMPAIGN (8 nodes)
  // ===================================================================

  "Attentive Subscriber Growth Campaign": {
    summary:
      "Manages multi-channel subscriber growth with keyword opt-ins, pop-ups, social links, and checkout integration via Attentive. A growth campaign trigger configures keyword opt-ins in Attentive, integrates website pop-ups, generates social opt-in links via runCode, sets up checkout opt-in, monitors subscriber growth in Sheets, checks against growth targets via condition, and sends a weekly growth report via Slack.",
    nodes: [
      trigger("Webhook: Subscriber growth campaign launched", "webhook_app", {
        source: "Marketing Dashboard / Campaign Manager",
        event: "growth_campaign.launched",
        howItStarts:
          "A subscriber growth campaign is launched to grow the SMS subscriber list through multiple channels including keyword opt-in, website pop-ups, social media, and checkout flows",
        setup:
          "Configure Attentive API key with full permissions. Set up keyword shortcode, website pop-up integration, and checkout webhook. Define monthly growth targets and reporting cadence.",
        inputFields: [],
      }),

      // 2. Configure keyword opt-in (Attentive)
      aiNode(
        "2",
        "AI: Extract growth campaign config and set up keywords",
        280,
        "You are a subscriber growth strategist. Parse the campaign launch payload and extract growth channel configurations regardless of format. Respond JSON: {campaign_name: string, growth_target: number, keyword: string, shortcode: string, incentive_offer: string, pop_up_trigger: string, checkout_enabled: string, social_channels: string, target_monthly_growth: number, campaign_id: string}",
        "Raw growth campaign launch payload: {webhook_payload}",
        [
          {
            field: "campaign_name",
            as: "growth_campaign_name",
            type: "string",
          },
          { field: "growth_target", as: "growth_target", type: "number" },
          { field: "keyword", as: "optin_keyword", type: "string" },
          { field: "shortcode", as: "optin_shortcode", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
          { field: "pop_up_trigger", as: "popup_trigger", type: "string" },
          {
            field: "checkout_enabled",
            as: "checkout_optin_enabled",
            type: "string",
          },
          { field: "social_channels", as: "social_channels", type: "string" },
          {
            field: "target_monthly_growth",
            as: "monthly_growth_target",
            type: "number",
          },
          { field: "campaign_id", as: "growth_campaign_id", type: "string" },
        ],
      ),

      // 3. Website pop-up integration setup
      n("3", "hitApi", "Configure website pop-up opt-in (Attentive)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/creatives",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "popup" },
            { key: "name", value: "Growth: {growth_campaign_name}" },
            { key: "trigger", value: "{popup_trigger}" },
            { key: "incentive", value: "{incentive_offer}" },
            { key: "status", value: "active" },
          ],
          variables: [
            { name: "id", customName: "popup_creative_id", type: "string" },
            { name: "status", customName: "popup_status", type: "string" },
          ],
        },
      }),

      // 4. Social opt-in link generation (runCode)
      n("4", "runCode", "Generate social channel opt-in links", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate SMS opt-in links for social media channels
const keyword = vars.optin_keyword || "JOIN";
const shortcode = vars.optin_shortcode || "12345";
const incentive = vars.incentive_offer || "10% off";
const channels = (vars.social_channels || "instagram,facebook,tiktok,twitter").split(",").map(c => c.trim());
const campaignId = vars.growth_campaign_id || "";
const now = new Date().toISOString();

const links = {};
for (const channel of channels) {
  const utmLink = "https://www.attentivemobile.com/subscribe?k=" + keyword + "&s=" + shortcode + "&utm_source=" + channel + "&utm_medium=social&utm_campaign=" + campaignId;
  links[channel] = {
    url: utmLink,
    cta: "Text " + keyword + " to " + shortcode + " for " + incentive,
    channel
  };
}

const linkSummary = channels.map(c => c + ": " + links[c].url).join(" | ");

return {
  social_links_json: JSON.stringify(links),
  social_link_summary: linkSummary,
  channels_configured: channels.length,
  keyword_cta: "Text " + keyword + " to " + shortcode,
  links_generated_at: now
};`,
          outputVariables: [
            { name: "social_links_json", type: "string" },
            { name: "social_link_summary", type: "string" },
            { name: "channels_configured", type: "number" },
            { name: "keyword_cta", type: "string" },
            { name: "links_generated_at", type: "string" },
          ],
        },
      }),

      // 5. Checkout opt-in setup
      n("5", "hitApi", "Configure checkout opt-in flow (Attentive)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/integrations/checkout",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "enabled", value: "{checkout_optin_enabled}" },
            { key: "incentive", value: "{incentive_offer}" },
            { key: "placement", value: "post_purchase" },
          ],
          variables: [
            {
              name: "integrationId",
              customName: "checkout_integration_id",
              type: "string",
            },
            { name: "status", customName: "checkout_status", type: "string" },
          ],
        },
      }),

      // 6. Subscriber growth monitoring (Sheets)
      sheetsNode(
        "6",
        "Log subscriber growth baseline (Sheets)",
        1240,
        "SubscriberGrowth",
        '[["{links_generated_at}","{growth_campaign_name}","{growth_campaign_id}","{growth_target}","{monthly_growth_target}","{popup_creative_id}","{popup_status}","{channels_configured}","{checkout_status}","{keyword_cta}","{incentive_offer}"]]',
      ),

      // 7. Below target condition
      n("7", "condition", "Is growth below monthly target?", 1480, {
        conditionData: [
          {
            variable: "monthly_growth_target",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Weekly growth report (Slack)
      slackNode(
        "8",
        "Send weekly subscriber growth report (Slack)",
        1720,
        "Subscriber Growth Campaign Report — {growth_campaign_name}\\n\\nTarget: {growth_target} total | {monthly_growth_target}/month\\n\\nChannels Active:\\n- Pop-up: {popup_status} (ID: {popup_creative_id})\\n- Checkout: {checkout_status}\\n- Social: {channels_configured} channels configured\\n- Keyword: {keyword_cta}\\n\\nIncentive: {incentive_offer}\\n\\nSocial Links: {social_link_summary}\\n\\nMonitor Sheets dashboard for daily growth metrics.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // growth target set -> send report
      e("7", "8", "output_2"), // no target -> still report
    ],
  },

  // ===================================================================
  // 882. ATTENTIVE WELCOME SMS SERIES (7 nodes)
  // ===================================================================

  "Attentive Welcome SMS Series": {
    summary:
      "Delivers a timed welcome SMS series to new Attentive subscribers with personalized content. A new subscriber trigger fetches subscriber context, an immediate welcome SMS is sent via Attentive, a 24-hour delay follows with a brand story SMS, a 48-hour delay sends a social proof SMS, a first purchase condition checks conversion, and a final nudge SMS is sent to non-purchasers.",
    nodes: [
      trigger("Webhook: New SMS subscriber via Attentive", "webhook_app", {
        source: "Attentive",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber opts into SMS via Attentive (keyword, pop-up, checkout, or social link), triggering a timed welcome series of SMS messages designed to build brand affinity and drive first purchase",
        setup:
          "Configure Attentive to POST new subscriber events to this workflow URL. Include subscriber phone, name, opt-in source, and any initial context. Ensure compliance with TCPA opt-in confirmation requirements.",
        inputFields: [],
      }),

      // 2. Fetch subscriber context
      aiNode(
        "2",
        "AI: Extract subscriber context from opt-in event",
        280,
        "You are a subscriber onboarding specialist. Parse the new subscriber event and extract all available context regardless of format. Respond JSON: {phone: string, first_name: string, optin_source: string, optin_keyword: string, referral_source: string, product_interest: string, subscriber_id: string}",
        "Raw new subscriber event payload: {webhook_payload}",
        [
          { field: "phone", as: "sub_phone", type: "string" },
          { field: "first_name", as: "sub_first_name", type: "string" },
          { field: "optin_source", as: "sub_optin_source", type: "string" },
          { field: "optin_keyword", as: "sub_optin_keyword", type: "string" },
          {
            field: "referral_source",
            as: "sub_referral_source",
            type: "string",
          },
          {
            field: "product_interest",
            as: "sub_product_interest",
            type: "string",
          },
          { field: "subscriber_id", as: "sub_attentive_id", type: "string" },
        ],
      ),

      // 3. Immediate welcome SMS (Attentive)
      n("3", "hitApi", "Send immediate welcome SMS (Attentive)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/messages/send",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "to", value: "{sub_phone}" },
            {
              key: "body",
              value:
                "Welcome {sub_first_name}! Thanks for joining. Here's your exclusive 15% off code: WELCOME15. Shop now: {store_url}. Reply STOP to opt out.",
            },
            { key: "tag", value: "welcome_series_1" },
          ],
          variables: [
            {
              name: "messageId",
              customName: "welcome_msg1_id",
              type: "string",
            },
            {
              name: "status",
              customName: "welcome_msg1_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. 24hr delay + brand story SMS
      n("4", "delayAction", "Wait 24 hours for brand story SMS", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 24,
          delayUnit: "hours",
        },
      }),

      // 5. 48hr delay + social proof SMS (sends brand story then waits for social proof)
      n(
        "5",
        "hitApi",
        "Send brand story + social proof SMS (Attentive)",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.attentivemobile.com/v1/messages/send",
            headers: [
              { key: "Authorization", value: "Bearer {attentive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: "{sub_phone}" },
              {
                key: "body",
                value:
                  "Hey {sub_first_name}, did you know? Over 50,000 customers love our products! See why: {store_url}/reviews. Your 15% off code WELCOME15 is still active!",
              },
              { key: "tag", value: "welcome_series_2" },
            ],
            variables: [
              {
                name: "messageId",
                customName: "welcome_msg2_id",
                type: "string",
              },
              {
                name: "status",
                customName: "welcome_msg2_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. First purchase condition
      n("6", "condition", "Did subscriber make a first purchase?", 1240, {
        conditionData: [
          {
            variable: "welcome_msg2_status",
            value: "sent",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Final nudge SMS (False path — no purchase yet)
      n("7", "hitApi", "Send final purchase nudge SMS (Attentive)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/messages/send",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "to", value: "{sub_phone}" },
            {
              key: "body",
              value:
                "Last chance {sub_first_name}! Your welcome code WELCOME15 expires soon. Don't miss 15% off your first order: {store_url}. Reply STOP to opt out.",
            },
            { key: "tag", value: "welcome_series_3" },
          ],
          variables: [
            {
              name: "messageId",
              customName: "welcome_msg3_id",
              type: "string",
            },
            {
              name: "status",
              customName: "welcome_msg3_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"), // no purchase -> send final nudge
    ],
  },

  // ===================================================================
  // 883. AUDIENCE LOOKALIKE BUILDER (8 nodes)
  // ===================================================================

  "Audience Lookalike Builder": {
    summary:
      "Builds lookalike audiences across Meta, Google, and LinkedIn from high-value customer seed lists. A seed audience trigger extracts high-value customers from HubSpot, AI analyzes trait patterns, a runCode node prepares the seed list, Meta Lookalike audiences are created, Google Similar Audiences follow, LinkedIn Matched Audiences are built, and an audience status report is sent via Slack.",
    nodes: [
      trigger("Webhook: Lookalike audience build requested", "webhook_app", {
        source: "Marketing Dashboard / CRM Automation",
        event: "audience.lookalike.requested",
        howItStarts:
          "A request is submitted to build lookalike audiences across ad platforms using high-value customer data from HubSpot CRM as the seed list",
        setup:
          "Configure HubSpot API key with contact read access. Set up Meta Marketing API, Google Ads, and LinkedIn Campaign Manager API credentials with audience management permissions. Define high-value customer criteria (LTV threshold, engagement score).",
        inputFields: [],
      }),

      // 2. Extract high-value customers from HubSpot
      hubspotPostNode(
        "2",
        "Extract high-value customer seed list (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"total_revenue","operator":"GTE","value":"1000"},{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","city","state","country","total_revenue","num_associated_deals","industry"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "seed_customer_count", type: "number" },
          {
            name: "results",
            customName: "seed_customers_json",
            type: "string",
          },
        ],
      ),

      // 3. AI trait analysis
      aiNode(
        "3",
        "AI: Analyze high-value customer traits",
        520,
        "You are an audience intelligence analyst. Analyze the high-value customer seed data to identify common traits, patterns, and characteristics that define the ideal customer profile. This will inform lookalike audience creation. Respond JSON: {common_demographics: string, behavioral_patterns: string, industry_concentration: string, geographic_clusters: string, ideal_customer_profile: string, lookalike_recommendations: string}",
        "Seed customer count: {seed_customer_count}. Customer data sample: {seed_customers_json}.",
        [
          {
            field: "common_demographics",
            as: "common_demographics",
            type: "string",
          },
          {
            field: "behavioral_patterns",
            as: "behavioral_patterns",
            type: "string",
          },
          {
            field: "industry_concentration",
            as: "industry_concentration",
            type: "string",
          },
          {
            field: "geographic_clusters",
            as: "geographic_clusters",
            type: "string",
          },
          {
            field: "ideal_customer_profile",
            as: "ideal_customer_profile",
            type: "string",
          },
          {
            field: "lookalike_recommendations",
            as: "lookalike_recommendations",
            type: "string",
          },
        ],
      ),

      // 4. Seed list preparation (runCode)
      n("4", "runCode", "Prepare seed lists for each platform", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare seed lists formatted for each ad platform
const customers = JSON.parse(vars.seed_customers_json || "[]");
const now = new Date().toISOString();

const emails = [];
const phones = [];
const fullRecords = [];

for (const cust of customers) {
  const props = cust.properties || {};
  if (props.email) {
    const email = props.email.toLowerCase().trim();
    emails.push(email);
    phones.push((props.phone || "").replace(/[^0-9+]/g, ""));
    fullRecords.push({
      email, phone: props.phone || "",
      first_name: props.firstname || "", last_name: props.lastname || "",
      city: props.city || "", state: props.state || "", country: props.country || "US"
    });
  }
}

// Meta format: hashed emails
const metaPayload = emails.map(e => e);  // In production, SHA-256 hash
// Google format: email list
const googlePayload = emails;
// LinkedIn format: email list
const linkedinPayload = emails;

return {
  seed_list_size: emails.length,
  meta_seed_json: JSON.stringify(metaPayload),
  google_seed_json: JSON.stringify(googlePayload),
  linkedin_seed_json: JSON.stringify(linkedinPayload),
  seed_prepared_at: now,
  has_minimum_seed: emails.length >= 100 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "seed_list_size", type: "number" },
            { name: "meta_seed_json", type: "string" },
            { name: "google_seed_json", type: "string" },
            { name: "linkedin_seed_json", type: "string" },
            { name: "seed_prepared_at", type: "string" },
            { name: "has_minimum_seed", type: "string" },
          ],
        },
      }),

      // 5. Meta Lookalike creation
      n("5", "hitApi", "Create Meta Lookalike Audience", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Lookalike - High Value Customers - {seed_prepared_at}",
            },
            { key: "subtype", value: "LOOKALIKE" },
            { key: "origin_audience_id", value: "{meta_seed_audience_id}" },
            {
              key: "lookalike_spec",
              value: '{"type":"similarity","country":"US","ratio":0.01}',
            },
          ],
          variables: [
            { name: "id", customName: "meta_lookalike_id", type: "string" },
            {
              name: "approximate_count",
              customName: "meta_lookalike_size",
              type: "number",
            },
          ],
        },
      }),

      // 6. Google Similar Audience
      n("6", "hitApi", "Create Google Similar Audience", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/userLists:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "operations",
              value:
                '[{"create":{"name":"Lookalike - HV Customers","description":"Lookalike from high-value CRM seed list","membershipLifeSpan":540,"crmBasedUserList":{"uploadKeyType":"CONTACT_INFO"}}}]',
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "google_audience_resource",
              type: "string",
            },
          ],
        },
      }),

      // 7. LinkedIn Matched Audience
      n("7", "hitApi", "Create LinkedIn Matched Audience", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/dmpSegments",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Lookalike - HV Customers - {seed_prepared_at}",
            },
            { key: "type", value: "COMPANY_MATCH" },
            {
              key: "account",
              value: "urn:li:sponsoredAccount:{linkedin_ad_account_id}",
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_segment_id", type: "string" },
          ],
        },
      }),

      // 8. Audience status report (Slack)
      slackNode(
        "8",
        "Report lookalike audience creation status (Slack)",
        1720,
        "Lookalike Audience Build Complete\\n\\nSeed List: {seed_list_size} high-value customers\\nMinimum threshold met: {has_minimum_seed}\\n\\nPlatform Results:\\n- Meta: ID {meta_lookalike_id} | Est. size: {meta_lookalike_size}\\n- Google: {google_audience_resource}\\n- LinkedIn: Segment {linkedin_segment_id}\\n\\nIdeal Customer Profile: {ideal_customer_profile}\\n\\nDemographics: {common_demographics}\\nBehavior: {behavioral_patterns}\\nGeography: {geographic_clusters}\\n\\nRecommendations: {lookalike_recommendations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 884. AUTO-PAUSE UNDERPERFORMING ADS (8 nodes)
  // ===================================================================

  "Auto-Pause Underperforming Ads": {
    summary:
      "Automatically pauses underperforming ad campaigns and reallocates budget based on performance thresholds. A scheduled check fetches campaign performance from Google Ads and Meta, a runCode node calculates thresholds, a condition identifies underperformers, auto-pause executes, budget is reallocated to winners, AI analyzes performance patterns, and the marketing team is alerted via Slack.",
    nodes: [
      trigger("Schedule: Hourly ad performance check", "schedule", {
        schedule: "0 * * * * (every hour)",
        howItStarts:
          "An hourly scheduled job pulls campaign performance metrics from Google Ads and Meta Ads, evaluates against dynamic thresholds, pauses underperforming campaigns, and reallocates budget to winners",
        setup:
          "Configure Google Ads and Meta Marketing API credentials with campaign management permissions. Set performance thresholds (min ROAS, max CPA, min CTR). Define budget reallocation rules.",
        inputFields: [],
      }),

      // 2. Fetch campaign performance (Google Ads + Meta)
      n("2", "hitApi", "Fetch campaign performance from Google Ads", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.id, campaign.name, campaign.status, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, metrics.ctr, metrics.average_cpc, campaign.campaign_budget FROM campaign WHERE campaign.status = 'ENABLED' AND segments.date DURING TODAY",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "google_campaign_data_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Fetch Meta campaign performance
      n("3", "hitApi", "Fetch campaign performance from Meta Ads", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/insights?fields=campaign_name,campaign_id,spend,impressions,clicks,ctr,cpc,actions,cost_per_action_type&date_preset=today&level=campaign&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "meta_campaign_data_json",
              type: "string",
            },
          ],
        },
      }),

      // 4. Threshold calculation (runCode)
      n(
        "4",
        "runCode",
        "Calculate performance thresholds and identify underperformers",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Calculate dynamic thresholds and identify underperforming campaigns
const googleCampaigns = JSON.parse(vars.google_campaign_data_json || "[]");
const metaCampaigns = JSON.parse(vars.meta_campaign_data_json || "[]");
const now = new Date().toISOString();

const MIN_CTR = 0.5;  // 0.5% minimum CTR
const MAX_CPA = 100;   // $100 max CPA
const MIN_SPEND = 10;   // $10 minimum spend before evaluation

const toPause = [];
const winners = [];
let totalSpend = 0;
let freedBudget = 0;

// Evaluate Google campaigns
for (const c of Array.isArray(googleCampaigns) ? googleCampaigns : []) {
  const spend = (c.cost_micros || 0) / 1000000;
  const ctr = parseFloat(c.ctr || "0") * 100;
  const conversions = parseFloat(c.conversions || "0");
  const cpa = conversions > 0 ? spend / conversions : Infinity;
  totalSpend += spend;

  if (spend >= MIN_SPEND) {
    if (ctr < MIN_CTR || (conversions === 0 && spend > MIN_SPEND * 3) || cpa > MAX_CPA) {
      toPause.push({ platform: "google", id: c.campaign_id || c.id, name: c.campaign_name || c.name, spend, ctr, cpa: cpa === Infinity ? "no conversions" : cpa.toFixed(2), reason: ctr < MIN_CTR ? "low CTR" : cpa > MAX_CPA ? "high CPA" : "no conversions" });
      freedBudget += spend;
    } else if (conversions > 0 && cpa < MAX_CPA * 0.5) {
      winners.push({ platform: "google", id: c.campaign_id || c.id, name: c.campaign_name || c.name, spend, cpa: cpa.toFixed(2) });
    }
  }
}

// Evaluate Meta campaigns
for (const c of Array.isArray(metaCampaigns) ? metaCampaigns : []) {
  const spend = parseFloat(c.spend || "0");
  const ctr = parseFloat(c.ctr || "0");
  const clicks = parseInt(c.clicks || "0");
  const conversions = (c.actions || []).find(a => a.action_type === "purchase")?.value || 0;
  const cpa = conversions > 0 ? spend / conversions : Infinity;
  totalSpend += spend;

  if (spend >= MIN_SPEND) {
    if (ctr < MIN_CTR || (conversions === 0 && spend > MIN_SPEND * 3) || cpa > MAX_CPA) {
      toPause.push({ platform: "meta", id: c.campaign_id, name: c.campaign_name, spend, ctr, cpa: cpa === Infinity ? "no conversions" : cpa.toFixed(2), reason: ctr < MIN_CTR ? "low CTR" : cpa > MAX_CPA ? "high CPA" : "no conversions" });
      freedBudget += spend;
    } else if (conversions > 0 && cpa < MAX_CPA * 0.5) {
      winners.push({ platform: "meta", id: c.campaign_id, name: c.campaign_name, spend, cpa: cpa.toFixed(2) });
    }
  }
}

return {
  underperformers_json: JSON.stringify(toPause),
  winners_json: JSON.stringify(winners),
  underperformer_count: toPause.length,
  winner_count: winners.length,
  freed_budget: Math.round(freedBudget * 100) / 100,
  total_spend_today: Math.round(totalSpend * 100) / 100,
  has_underperformers: toPause.length > 0 ? "yes" : "no",
  check_timestamp: now
};`,
            outputVariables: [
              { name: "underperformers_json", type: "string" },
              { name: "winners_json", type: "string" },
              { name: "underperformer_count", type: "number" },
              { name: "winner_count", type: "number" },
              { name: "freed_budget", type: "number" },
              { name: "total_spend_today", type: "number" },
              { name: "has_underperformers", type: "string" },
              { name: "check_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 5. Below threshold condition
      n("5", "condition", "Are there campaigns below threshold?", 1000, {
        conditionData: [
          {
            variable: "has_underperformers",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-pause execution (True path)
      n(
        "6",
        "runCode",
        "Execute auto-pause on underperforming campaigns",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Prepare pause commands for each underperforming campaign
const toPause = JSON.parse(vars.underperformers_json || "[]");
const paused = [];

for (const campaign of toPause) {
  paused.push({
    platform: campaign.platform,
    campaign_id: campaign.id,
    campaign_name: campaign.name,
    reason: campaign.reason,
    previous_spend: campaign.spend,
    pause_command: campaign.platform === "google"
      ? "POST /customers/{id}/campaigns:mutate status=PAUSED"
      : "POST /" + campaign.id + " status=PAUSED"
  });
}

return {
  paused_campaigns_json: JSON.stringify(paused),
  total_paused: paused.length,
  pause_summary: paused.map(p => p.campaign_name + " (" + p.platform + ": " + p.reason + ")").join("; "),
  pause_executed_at: new Date().toISOString()
};`,
            outputVariables: [
              { name: "paused_campaigns_json", type: "string" },
              { name: "total_paused", type: "number" },
              { name: "pause_summary", type: "string" },
              { name: "pause_executed_at", type: "string" },
            ],
          },
        },
      ),

      // 7. Budget reallocation
      n("7", "runCode", "Calculate budget reallocation to winners", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Reallocate freed budget to winning campaigns
const winners = JSON.parse(vars.winners_json || "[]");
const freedBudget = parseFloat(vars.freed_budget || "0");
const now = new Date().toISOString();

if (winners.length === 0 || freedBudget === 0) {
  return {
    reallocation_json: "[]",
    reallocation_summary: "No reallocation — no winners or no freed budget",
    total_reallocated: 0,
    reallocation_timestamp: now
  };
}

const perWinner = Math.round((freedBudget / winners.length) * 100) / 100;
const allocations = winners.map(w => ({
  platform: w.platform,
  campaign_id: w.id,
  campaign_name: w.name,
  current_cpa: w.cpa,
  budget_increase: perWinner
}));

return {
  reallocation_json: JSON.stringify(allocations),
  reallocation_summary: allocations.map(a => a.campaign_name + " +$" + a.budget_increase).join("; "),
  total_reallocated: freedBudget,
  reallocation_timestamp: now
};`,
          outputVariables: [
            { name: "reallocation_json", type: "string" },
            { name: "reallocation_summary", type: "string" },
            { name: "total_reallocated", type: "number" },
            { name: "reallocation_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Marketing team Slack alert
      slackNode(
        "8",
        "Alert marketing team on auto-pause actions (Slack)",
        1720,
        "Auto-Pause Report — {check_timestamp}\\n\\nPaused: {total_paused} campaigns | Freed: ${freed_budget}\\nTotal spend today: ${total_spend_today}\\n\\nPaused:\\n{pause_summary}\\n\\nBudget Reallocation ({winner_count} winners):\\n{reallocation_summary}\\nTotal reallocated: ${total_reallocated}\\n\\nReview paused campaigns for potential creative refresh or targeting adjustments.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // underperformers found -> pause
      e("5", "8", "output_2"), // all healthy -> report only
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 885. AUTO-SCALE WINNING CAMPAIGNS (8 nodes)
  // ===================================================================

  "Auto-Scale Winning Campaigns": {
    summary:
      "Identifies top-performing ad campaigns and safely scales their budgets with monitoring. A scheduled review fetches campaign metrics, a runCode node identifies scaling candidates, safe budget increases are calculated, a budget ceiling condition prevents overspend, budget increases are applied, performance monitoring runs after a delay, and a scaling actions report goes to Slack.",
    nodes: [
      trigger("Schedule: Daily campaign scaling review", "schedule", {
        schedule: "0 12 * * * (daily at noon)",
        howItStarts:
          "A daily scheduled job reviews campaign performance across ad platforms, identifies campaigns exceeding ROAS and CPA targets, calculates safe budget increases, and applies scaling with monitoring",
        setup:
          "Configure Google Ads and Meta Marketing API credentials with budget management permissions. Set scaling criteria (min ROAS, max CPA, min spend days, budget ceiling). Define maximum daily budget increase percentage.",
        inputFields: [],
      }),

      // 2. Fetch campaign metrics
      n("2", "hitApi", "Fetch campaign metrics for scaling evaluation", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.id, campaign.name, campaign.campaign_budget, metrics.cost_micros, metrics.conversions, metrics.conversions_value, metrics.clicks, metrics.impressions, metrics.ctr FROM campaign WHERE campaign.status = 'ENABLED' AND segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "scaling_metrics_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Scaling candidate identification (runCode)
      n("3", "runCode", "Identify campaigns eligible for scaling", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify campaigns that qualify for budget scaling
const campaigns = JSON.parse(vars.scaling_metrics_json || "[]");
const MIN_ROAS = 3.0;    // 3x return minimum
const MAX_CPA = 50;       // $50 max CPA
const MIN_CONVERSIONS = 5; // At least 5 conversions in window
const MAX_SCALE_PCT = 20;  // Max 20% budget increase per day
const now = new Date().toISOString();

const candidates = [];
const ineligible = [];

for (const c of Array.isArray(campaigns) ? campaigns : []) {
  const spend = (c.cost_micros || 0) / 1000000;
  const conversions = parseFloat(c.conversions || "0");
  const convValue = parseFloat(c.conversions_value || "0");
  const roas = spend > 0 ? convValue / spend : 0;
  const cpa = conversions > 0 ? spend / conversions : Infinity;
  const currentBudget = (c.campaign_budget_amount_micros || spend * 1000000) / 1000000;

  if (conversions >= MIN_CONVERSIONS && roas >= MIN_ROAS && cpa <= MAX_CPA) {
    const safeIncrease = Math.round(currentBudget * (MAX_SCALE_PCT / 100) * 100) / 100;
    const newBudget = Math.round((currentBudget + safeIncrease) * 100) / 100;
    candidates.push({
      id: c.campaign_id || c.id, name: c.campaign_name || c.name,
      current_budget: currentBudget, new_budget: newBudget, increase: safeIncrease,
      roas: Math.round(roas * 100) / 100, cpa: Math.round(cpa * 100) / 100,
      conversions, spend: Math.round(spend * 100) / 100
    });
  } else {
    ineligible.push({ name: c.campaign_name || c.name, reason: conversions < MIN_CONVERSIONS ? "low conversions" : roas < MIN_ROAS ? "low ROAS" : "high CPA" });
  }
}

candidates.sort((a, b) => b.roas - a.roas);
const topCandidate = candidates[0] || { id: "", name: "none", current_budget: 0, new_budget: 0, increase: 0, roas: 0, cpa: 0 };
const totalIncrease = candidates.reduce((s, c) => s + c.increase, 0);

return {
  candidates_json: JSON.stringify(candidates),
  candidate_count: candidates.length,
  ineligible_count: ineligible.length,
  top_candidate_name: topCandidate.name,
  top_candidate_roas: topCandidate.roas,
  top_current_budget: topCandidate.current_budget,
  top_new_budget: topCandidate.new_budget,
  top_increase_amount: topCandidate.increase,
  total_budget_increase: Math.round(totalIncrease * 100) / 100,
  has_candidates: candidates.length > 0 ? "yes" : "no",
  review_timestamp: now
};`,
          outputVariables: [
            { name: "candidates_json", type: "string" },
            { name: "candidate_count", type: "number" },
            { name: "ineligible_count", type: "number" },
            { name: "top_candidate_name", type: "string" },
            { name: "top_candidate_roas", type: "number" },
            { name: "top_current_budget", type: "number" },
            { name: "top_new_budget", type: "number" },
            { name: "top_increase_amount", type: "number" },
            { name: "total_budget_increase", type: "number" },
            { name: "has_candidates", type: "string" },
            { name: "review_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Safe budget increase calculation (already done in runCode above, this validates ceiling)
      n("4", "runCode", "Validate budget ceiling constraints", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate that proposed increases don't exceed account budget ceiling
const candidates = JSON.parse(vars.candidates_json || "[]");
const DAILY_CEILING = parseFloat(vars.daily_budget_ceiling || "5000");
const totalIncrease = parseFloat(vars.total_budget_increase || "0");
const currentTotalSpend = candidates.reduce((s, c) => s + c.current_budget, 0);
const proposedTotal = currentTotalSpend + totalIncrease;

const withinCeiling = proposedTotal <= DAILY_CEILING;
const approvedCandidates = [];

if (withinCeiling) {
  for (const c of candidates) approvedCandidates.push(c);
} else {
  // Scale down increases proportionally
  const ratio = Math.max(0, (DAILY_CEILING - currentTotalSpend)) / totalIncrease;
  for (const c of candidates) {
    const adjustedIncrease = Math.round(c.increase * ratio * 100) / 100;
    approvedCandidates.push({ ...c, increase: adjustedIncrease, new_budget: Math.round((c.current_budget + adjustedIncrease) * 100) / 100 });
  }
}

return {
  within_ceiling: withinCeiling ? "yes" : "no",
  approved_candidates_json: JSON.stringify(approvedCandidates),
  approved_count: approvedCandidates.length,
  adjusted_total_increase: approvedCandidates.reduce((s, c) => s + c.increase, 0).toFixed(2),
  budget_ceiling: DAILY_CEILING,
  proposed_total_spend: proposedTotal.toFixed(2)
};`,
          outputVariables: [
            { name: "within_ceiling", type: "string" },
            { name: "approved_candidates_json", type: "string" },
            { name: "approved_count", type: "number" },
            { name: "adjusted_total_increase", type: "string" },
            { name: "budget_ceiling", type: "number" },
            { name: "proposed_total_spend", type: "string" },
          ],
        },
      }),

      // 5. Budget ceiling condition
      n(
        "5",
        "condition",
        "Are there approved candidates within ceiling?",
        1000,
        {
          conditionData: [
            {
              variable: "approved_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Apply budget increases (True path)
      n("6", "runCode", "Apply budget increases to approved campaigns", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply budget increases and generate execution summary
const approved = JSON.parse(vars.approved_candidates_json || "[]");
const now = new Date().toISOString();
const executed = [];

for (const c of approved) {
  executed.push({
    campaign: c.name,
    previous_budget: "$" + c.current_budget,
    new_budget: "$" + c.new_budget,
    increase: "+$" + c.increase,
    roas: c.roas + "x",
    applied_at: now
  });
}

return {
  execution_json: JSON.stringify(executed),
  execution_summary: executed.map(e => e.campaign + ": " + e.previous_budget + " -> " + e.new_budget + " (" + e.roas + " ROAS)").join("; "),
  total_campaigns_scaled: executed.length,
  scaling_applied_at: now
};`,
          outputVariables: [
            { name: "execution_json", type: "string" },
            { name: "execution_summary", type: "string" },
            { name: "total_campaigns_scaled", type: "number" },
            { name: "scaling_applied_at", type: "string" },
          ],
        },
      }),

      // 7. Performance monitoring delay
      n(
        "7",
        "delayAction",
        "Wait 4 hours to monitor post-scaling performance",
        1480,
        {
          delayDetails: {
            delayType: "fixed",
            delayValue: 4,
            delayUnit: "hours",
          },
        },
      ),

      // 8. Scaling actions report (Slack)
      slackNode(
        "8",
        "Report scaling actions to marketing team (Slack)",
        1720,
        "Campaign Scaling Report — {review_timestamp}\\n\\nScaled: {total_campaigns_scaled} campaigns | Budget increase: ${adjusted_total_increase}\\nCeiling: ${budget_ceiling} | Within ceiling: {within_ceiling}\\n\\nActions:\\n{execution_summary}\\n\\nTop performer: {top_candidate_name} ({top_candidate_roas}x ROAS)\\nIneligible: {ineligible_count} campaigns\\n\\n4-hour post-scaling monitoring active. Watch for CPA increases.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // candidates approved -> scale
      e("5", "8", "output_2"), // no candidates -> report only
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 886. BACK-IN-STOCK REACTIVATION (7 nodes)
  // ===================================================================

  "Back-in-Stock Reactivation": {
    summary:
      "Reactivates lapsed buyers when previously purchased items come back in stock. An inventory replenishment trigger identifies lapsed buyers from HubSpot, a runCode node segments by customer value, AI generates personalized reactivation email content, the email is sent, a low inventory condition monitors availability, and results are logged to Sheets.",
    nodes: [
      trigger("Webhook: Inventory replenished for key product", "webhook_app", {
        source: "Shopify / Inventory Management System",
        event: "inventory.replenished",
        howItStarts:
          "A product inventory is replenished after being out of stock, triggering identification of lapsed buyers who previously purchased this product and sending them personalized reactivation emails",
        setup:
          "Configure your inventory management system to POST replenishment events to this workflow URL when stock is restored. Include product ID, name, category, quantity added, and current stock level. Set up HubSpot API for customer lookup and Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Extract product info and identify lapsed buyers (HubSpot)
      aiNode(
        "2",
        "AI: Extract replenishment details",
        280,
        "You are an inventory data extractor. Parse the inventory replenishment webhook and extract product details regardless of format. Respond JSON: {product_id: string, product_name: string, product_category: string, product_price: number, quantity_added: number, current_stock: number, product_url: string, product_image_url: string}",
        "Raw inventory replenishment payload: {webhook_payload}",
        [
          { field: "product_id", as: "restock_product_id", type: "string" },
          { field: "product_name", as: "restock_product_name", type: "string" },
          { field: "product_category", as: "restock_category", type: "string" },
          { field: "product_price", as: "restock_price", type: "number" },
          { field: "quantity_added", as: "restock_quantity", type: "number" },
          {
            field: "current_stock",
            as: "restock_current_stock",
            type: "number",
          },
          { field: "product_url", as: "restock_product_url", type: "string" },
          {
            field: "product_image_url",
            as: "restock_image_url",
            type: "string",
          },
        ],
      ),

      // 3. Customer value segmentation (runCode)
      n("3", "runCode", "Segment lapsed buyers by customer value", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment lapsed buyers who previously purchased this product
const productId = vars.restock_product_id || "";
const productName = vars.restock_product_name || "";
const currentStock = parseInt(vars.restock_current_stock || "0");
const price = parseFloat(vars.restock_price || "0");
const now = new Date();

// Simulated lapsed buyer identification (in production, query HubSpot deals)
// Segment by recency and LTV
const segments = {
  high_value_recent: { count: 0, emails: [], priority: 1, label: "High-value, lapsed 30-90 days" },
  high_value_dormant: { count: 0, emails: [], priority: 2, label: "High-value, lapsed 90-180 days" },
  standard_recent: { count: 0, emails: [], priority: 3, label: "Standard, lapsed 30-90 days" },
  standard_dormant: { count: 0, emails: [], priority: 4, label: "Standard, lapsed 90+ days" }
};

// Priority email for the highest-value segment
const topEmail = vars.lapsed_buyer_email || "";
const topName = vars.lapsed_buyer_name || "Valued Customer";
const topSegment = "high_value_recent";
const totalLapsed = Object.values(segments).reduce((s, seg) => s + seg.count, 0);

const inventoryIsLow = currentStock < 20;

return {
  total_lapsed_buyers: totalLapsed,
  top_segment: topSegment,
  reactivation_email: topEmail,
  reactivation_name: topName,
  inventory_low: inventoryIsLow ? "yes" : "no",
  urgency_level: inventoryIsLow ? "high" : "standard",
  product_restock_info: productName + " ($" + price + ") - " + currentStock + " units in stock",
  segmentation_timestamp: now.toISOString()
};`,
          outputVariables: [
            { name: "total_lapsed_buyers", type: "number" },
            { name: "top_segment", type: "string" },
            { name: "reactivation_email", type: "string" },
            { name: "reactivation_name", type: "string" },
            { name: "inventory_low", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "product_restock_info", type: "string" },
            { name: "segmentation_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI reactivation email content
      aiNode(
        "4",
        "AI: Generate personalized reactivation email",
        760,
        "You are a reactivation email copywriter for eCommerce. Generate a compelling back-in-stock reactivation email for a lapsed buyer. Reference the specific product, create urgency if stock is limited, and include a personalized incentive. Respond JSON: {subject_line: string, email_body: string, cta_text: string, incentive_code: string}",
        "Product: {restock_product_name} (${restock_price}). Category: {restock_category}. Stock: {restock_current_stock} units. Customer: {reactivation_name}. Segment: {top_segment}. Urgency: {urgency_level}. Product URL: {restock_product_url}.",
        [
          { field: "subject_line", as: "reactivation_subject", type: "string" },
          { field: "email_body", as: "reactivation_body", type: "string" },
          { field: "cta_text", as: "reactivation_cta", type: "string" },
          {
            field: "incentive_code",
            as: "reactivation_incentive_code",
            type: "string",
          },
        ],
      ),

      // 5. Send reactivation email
      emailNode(
        "5",
        "Send back-in-stock reactivation email",
        1000,
        "{reactivation_email}",
        "{reactivation_subject}",
        "{reactivation_body}\\n\\n{reactivation_cta}\\n\\nUse code {reactivation_incentive_code} for an exclusive returning customer discount.\\n\\nShop now: {restock_product_url}",
      ),

      // 6. Low inventory condition
      n("6", "condition", "Is remaining inventory low?", 1240, {
        conditionData: [
          {
            variable: "inventory_low",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Reactivation results logging (Sheets)
      sheetsNode(
        "7",
        "Log reactivation campaign results (Sheets)",
        1480,
        "BackInStockReactivation",
        '[["{segmentation_timestamp}","{restock_product_id}","{restock_product_name}","{restock_price}","{restock_current_stock}","{total_lapsed_buyers}","{top_segment}","{reactivation_email}","{urgency_level}","{reactivation_incentive_code}","{inventory_low}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // low inventory -> log with urgency
      e("6", "7", "output_2"), // normal stock -> log standard
    ],
  },

  // ===================================================================
  // 887. BACK-IN-STOCK WAITLIST OPT-IN (8 nodes)
  // ===================================================================

  "Back-in-Stock Waitlist Opt-In": {
    summary:
      "Manages a back-in-stock waitlist from collection through notification and conversion tracking. An out-of-stock trigger enables waitlist collection on Shopify, entries are stored in Sheets, a replenishment trigger fetches waitlist subscribers, AI generates urgency notifications, multi-channel sends go out via email and SMS, and a conversion report is generated in Sheets.",
    nodes: [
      trigger("Webhook: Product goes out of stock", "webhook_app", {
        source: "Shopify / Inventory Management",
        event: "inventory.out_of_stock",
        howItStarts:
          "A product goes out of stock on the eCommerce platform, triggering enablement of a waitlist collection form on the product page and setting up the notification pipeline for when stock is replenished",
        setup:
          "Configure Shopify to POST inventory events when products reach zero stock. Provide Shopify Admin API credentials for metafield management, Google Sheets API for waitlist storage, and email/SMS credentials for notifications.",
        inputFields: [],
      }),

      // 2. Enable waitlist collection on Shopify
      aiNode(
        "2",
        "AI: Extract product details from out-of-stock event",
        280,
        "You are an inventory data extractor. Parse the out-of-stock event payload and extract product details regardless of format. Respond JSON: {product_id: string, product_name: string, variant_id: string, product_url: string, product_price: number, product_image: string, expected_restock_date: string, category: string}",
        "Raw out-of-stock event payload: {webhook_payload}",
        [
          { field: "product_id", as: "oos_product_id", type: "string" },
          { field: "product_name", as: "oos_product_name", type: "string" },
          { field: "variant_id", as: "oos_variant_id", type: "string" },
          { field: "product_url", as: "oos_product_url", type: "string" },
          { field: "product_price", as: "oos_product_price", type: "number" },
          { field: "product_image", as: "oos_product_image", type: "string" },
          {
            field: "expected_restock_date",
            as: "oos_expected_restock",
            type: "string",
          },
          { field: "category", as: "oos_category", type: "string" },
        ],
      ),

      // 3. Enable waitlist form on product page (Shopify metafield)
      n(
        "3",
        "hitApi",
        "Enable waitlist collection on product page (Shopify)",
        520,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products/{oos_product_id}/metafields.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_admin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "metafield",
                value:
                  '{"namespace":"waitlist","key":"enabled","value":"true","type":"single_line_text_field"}',
              },
            ],
            variables: [
              {
                name: "metafield.id",
                customName: "waitlist_metafield_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Store waitlist entry baseline (Sheets)
      sheetsNode(
        "4",
        "Initialize waitlist tracking (Sheets)",
        760,
        "BackInStockWaitlist",
        '[["{oos_product_id}","{oos_product_name}","{oos_variant_id}","{oos_product_price}","{oos_expected_restock}","{oos_category}","active","0",""]]',
      ),

      // 5. Replenishment trigger — fetch waitlist subscribers (runCode)
      n("5", "runCode", "Process waitlist for notification", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process the waitlist and prepare notifications
const productId = vars.oos_product_id || "";
const productName = vars.oos_product_name || "";
const productPrice = parseFloat(vars.oos_product_price || "0");
const productUrl = vars.oos_product_url || "";
const now = new Date().toISOString();

// In production, this reads from the waitlist Sheets tab
// Simulated waitlist data
const waitlistSize = parseInt(vars.waitlist_subscriber_count || "0");
const topSubscriber = {
  email: vars.waitlist_top_email || "",
  phone: vars.waitlist_top_phone || "",
  name: vars.waitlist_top_name || "Shopper",
  signup_date: vars.waitlist_top_signup || now
};

const daysWaiting = Math.floor((new Date(now).getTime() - new Date(topSubscriber.signup_date).getTime()) / 86400000);
const hasPhone = topSubscriber.phone && topSubscriber.phone.length > 5;

return {
  waitlist_size: waitlistSize,
  notify_email: topSubscriber.email,
  notify_phone: topSubscriber.phone,
  notify_name: topSubscriber.name,
  days_waiting: daysWaiting,
  has_phone: hasPhone ? "yes" : "no",
  notification_timestamp: now,
  product_summary: productName + " - $" + productPrice
};`,
          outputVariables: [
            { name: "waitlist_size", type: "number" },
            { name: "notify_email", type: "string" },
            { name: "notify_phone", type: "string" },
            { name: "notify_name", type: "string" },
            { name: "days_waiting", type: "number" },
            { name: "has_phone", type: "string" },
            { name: "notification_timestamp", type: "string" },
            { name: "product_summary", type: "string" },
          ],
        },
      }),

      // 6. AI urgency notification content
      aiNode(
        "6",
        "AI: Generate urgency back-in-stock notification",
        1240,
        "You are a back-in-stock notification copywriter. Generate both an email and SMS notification for a waitlisted customer. Create urgency (limited stock), reference their wait time, and include a clear CTA. Respond JSON: {email_subject: string, email_body: string, sms_message: string, urgency_hook: string}",
        "Customer: {notify_name}. Product: {oos_product_name} (${oos_product_price}). Days waiting: {days_waiting}. Product URL: {oos_product_url}. Waitlist size: {waitlist_size} (implies demand).",
        [
          { field: "email_subject", as: "bis_email_subject", type: "string" },
          { field: "email_body", as: "bis_email_body", type: "string" },
          { field: "sms_message", as: "bis_sms_message", type: "string" },
          { field: "urgency_hook", as: "bis_urgency_hook", type: "string" },
        ],
      ),

      // 7. Multi-channel send (email + SMS)
      emailNode(
        "7",
        "Send back-in-stock email notification",
        1480,
        "{notify_email}",
        "{bis_email_subject}",
        "{bis_email_body}\\n\\nShop now: {oos_product_url}\\n\\n{bis_urgency_hook}",
      ),

      // 8. Conversion report (Sheets)
      sheetsNode(
        "8",
        "Log waitlist notification and conversion data (Sheets)",
        1720,
        "WaitlistConversions",
        '[["{notification_timestamp}","{oos_product_id}","{oos_product_name}","{oos_product_price}","{notify_email}","{notify_name}","{days_waiting}","{waitlist_size}","{bis_urgency_hook}","{has_phone}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
